import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FloppyDisk, ArrowsCounterClockwise, X, CaretUp, CaretDown, CaretUpDown, Trash, CalendarBlank } from "@phosphor-icons/react";
import { AGENT_REGISTRY, LAYER_LABELS } from "../lib/agentRegistry";

/* ═══════════════════════════════════════════════════════════════════
 * Admin: usage tabs — Áttekintés, Tesztelők (usage), Log, Beállítások, Ügynökök
 * Sprint 2: ECU + MEX kredit oszlopok, rendezhető fejléc, user drill-down.
 * ═══════════════════════════════════════════════════════════════════ */

const fmtUsd = (n) => `$${(n ?? 0).toFixed(2)}`;
const fmtUsd4 = (n) => `$${(n ?? 0).toFixed(4)}`;
const fmtMex = (n) => (n ?? 0).toFixed(1);
const fmtEcu = (n) => (n ?? 0).toFixed(2);
const fmtFactor = (n) => `${(n ?? 1).toFixed(2)}x`;
const fmtTs = (s) => (s ? new Date(s).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const normalizeAdminUsageMode = (mode) => (mode === "estimated" ? "estimated" : "calibrated");
const FACTOR_SOURCE_META = {
  exact: { color: "#4ade80", label: "exact" },
  rolling: { color: "#fbbf24", label: "rolling" },
  raw_1x: { color: "#7c7c8a", label: "raw 1.0x" },
  pending: { color: "#fb7185", label: "pending" },
};
const getFactorSourceMeta = (source) => FACTOR_SOURCE_META[source] || FACTOR_SOURCE_META.raw_1x;
const deriveCorrectionFactor = (rawUsd, vfUsd) => {
  const raw = Number(rawUsd || 0);
  const corrected = Number(vfUsd || 0);
  if (!raw || raw <= 0) return 1;
  const factor = corrected / raw;
  return Number.isFinite(factor) && factor > 0 ? factor : 1;
};
const ADMIN_USAGE_REFRESH_EVENT = "admin-usage-refresh";

const emitAdminUsageRefresh = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(ADMIN_USAGE_REFRESH_EVENT));
  }
};

const useAdminUsageRefresh = (handler) => {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.addEventListener(ADMIN_USAGE_REFRESH_EVENT, handler);
    return () => window.removeEventListener(ADMIN_USAGE_REFRESH_EVENT, handler);
  }, [handler]);
};

// Sprint 3.5 finomítás: technikai nevek → magyar emberi címkék
const AGENT_LABEL = {
  brand_voice: "Márkahang",
  avatar:      "Avatar",
  offer:       "Ajánlat",
  magnet_post: "Poszt Varázsló",
  sales:       "Sales Mesterműhely",
  webinar:     "Webinár Varázsló",
  landing:     "Landing Varázsló",
  lead_magnet: "Ügyfélmágnes Varázsló",
  newsletter:  "Email Varázsló",
  foundation:  "F3 alap",
  unknown:     "Ismeretlen",
};
const SUBAGENT_LABEL = {
  "post-facebook": "Facebook poszt",
  "post-linkedin": "LinkedIn poszt",
  "post-reels":    "Reels poszt",
  "post-carousel": "Carousel poszt",
};
const REQTYPE_LABEL = {
  topic_ideas:   "Témaötletek",
  post_outlines: "Posztvázlatok",
  final_post:    "Kész poszt",
  refine:        "Finomítás",
  generate:      "Generálás",
  save:          "Mentés",
};
const LAYER_HU = { foundation: "Alapozás", attraction: "Vonzás", conversion: "Konverzió" };
const labelAgent   = (k) => AGENT_LABEL[k]    || (k || "—");
const labelSub     = (k) => SUBAGENT_LABEL[k] || (k || "—");
const labelReq     = (k) => REQTYPE_LABEL[k]  || (k || "—");
const labelLayer   = (k) => LAYER_HU[k]       || (k || "—");
const WORKFLOW_DATA_STATUS_LABEL = {
  "3d_rolling": { color: "#4ade80", label: "3n rolling átlag" },
  "latest_only": { color: "#fbbf24", label: "Legutóbbi teljes futás" },
  "partial_only": { color: "#a1a1a1", label: "Csak részadat" },
  "no_data": { color: "#7c7c8a", label: "Nincs elég adat" },
  "enough_data": { color: "#4ade80", label: "Elegendő adat" },
  "partial_data": { color: "#a1a1a1", label: "Csak részadat" },
  "suspicious_high": { color: "#fb7185", label: "Gyanúsan magas" },
  "suspicious_low": { color: "#fb7185", label: "Gyanúsan alacsony" },
};

const getBudgetStatus = (used, cap) => {
  if (!cap || cap <= 0) return "ok";
  const pct = (Number(used || 0) / Number(cap || 0)) * 100;
  if (pct >= 100) return "reached";
  if (pct >= 80) return "near";
  return "ok";
};

const getStatusColor = (status, isAdmin = false) => {
  if (isAdmin) return "#a294ff";
  if (status === "reached") return "#fb7185";
  if (status === "near") return "#fbbf24";
  return "#ffdb5c";
};

const getUserUsageDisplay = (row, mode = "calibrated") => {
  const normalizedMode = normalizeAdminUsageMode(mode);
  const estimatedMode = normalizedMode === "estimated";
  const monthUsedUsd = estimatedMode ? Number(row?.month_cost || 0) : Number(row?.month_vf_cost || 0);
  const todayUsedUsd = estimatedMode ? Number(row?.today_cost || 0) : Number(row?.today_vf_cost || 0);
  const effectiveMonthlyUsd = Number(row?.effective_monthly_usd || 0);
  const remainingUsd = Math.max(effectiveMonthlyUsd - monthUsedUsd, 0);
  return {
    monthUsedUsd,
    todayUsedUsd,
    monthUsedMxc: estimatedMode ? Number(row?.month_mex_credit || 0) : Number(row?.month_vf_mex_credit || 0),
    todayUsedMxc: estimatedMode ? Number(row?.today_mex_credit || 0) : Number(row?.today_vf_mex_credit || 0),
    monthEcu: estimatedMode ? Number(row?.month_estimated_ecu || 0) : Number(row?.month_ecu || 0),
    todayEcu: estimatedMode ? Number(row?.today_estimated_ecu || 0) : Number(row?.today_ecu || 0),
    remainingUsd,
    remainingMxc: Number((remainingUsd * Number(row?.mex_per_usd || 50)).toFixed(4)),
    monthStatus: getBudgetStatus(monthUsedUsd, effectiveMonthlyUsd),
    dayStatus: getBudgetStatus(todayUsedUsd, Number(row?.daily_cap_usd || 0)),
  };
};

const getUsageLogDisplay = (log, mode = "calibrated") => {
  const normalizedMode = normalizeAdminUsageMode(mode);
  const estimatedMode = normalizedMode === "estimated";
  return {
    ecu: estimatedMode ? Number(log?.estimated_ecu || 0) : Number(log?.calibrated_ecu ?? log?.estimated_ecu ?? 0),
    usd: estimatedMode ? Number(log?.estimated_usd || 0) : Number(log?.effective_usd ?? log?.vf_usd ?? 0),
    mxc: estimatedMode ? Number(log?.estimated_mxc || 0) : Number(log?.effective_mxc ?? log?.vf_mxc ?? 0),
    factor: estimatedMode ? 1 : Number(log?.correction_factor || 1),
    source: estimatedMode ? "raw_1x" : (log?.correction_source || "raw_1x"),
    sourceLabel: estimatedMode ? "Raw becslés" : ((log?.correction_source === "exact" || log?.correction_source === "rolling") ? "Valós ECU réteg" : log?.correction_source === "pending" ? "Reconciliation pending" : "Raw fallback"),
  };
};

const getWorkflowMetric = (wf, mode = "calibrated") => {
  const useCalibrated = mode === "calibrated";
  const hasFullRuns = (wf?.full_run_count || 0) > 0;
  if (hasFullRuns) {
    return {
      primaryMxc: useCalibrated ? (wf?.average_full_run_vf_mxc || 0) : (wf?.average_full_run_cost_mxc || 0),
      secondaryUsd: useCalibrated ? (wf?.average_full_run_vf_usd || 0) : (wf?.average_full_run_cost_usd || 0),
      secondaryEcu: useCalibrated ? (wf?.average_full_run_actual_platform_ecu || 0) : (wf?.average_full_run_estimated_ecu || 0),
      helperLabel: "Átlag / teljes futás",
    };
  }
  return null;
};

function OverviewModeSwitch({ mode, onChange }) {
  const isEstimated = mode === "estimated";
  return (
    <div className="inline-flex items-center rounded-full border border-white/[0.08] p-1" style={{ background: "rgba(20,20,26,0.75)" }} data-testid="overview-mode-switch">
      <button
        type="button"
        onClick={() => onChange("estimated")}
        className="px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors"
        style={{ color: isEstimated ? "#111" : "#a1a1a1", background: isEstimated ? "#f3f0ff" : "transparent" }}
        data-testid="overview-mode-estimated-button"
      >
        Becsült
      </button>
      <button
        type="button"
        onClick={() => onChange("calibrated")}
        className="px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors"
        style={{ color: !isEstimated ? "#111" : "#a1a1a1", background: !isEstimated ? "#f3f0ff" : "transparent" }}
        data-testid="overview-mode-calibrated-button"
      >
        Valós
      </button>
    </div>
  );
}

/* ─────────── Overview tab ─────────── */
export function UsageOverviewTab({ mode = "calibrated", onModeChange }) {
  const [data, setData] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [busy, setBusy] = useState(true);
  const normalizedMode = normalizeAdminUsageMode(mode);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [o, cov] = await Promise.allSettled([
        api.get("/admin/usage/overview"),
        api.get("/admin/workflow-coverage?include_admin_runs=true&rolling_days=3&window_days=30"),
      ]);
      if (o.status === "fulfilled") setData(o.value.data);
      else throw new Error("overview failed");
      if (cov.status === "fulfilled") setCoverage(cov.value.data);
      else throw new Error("coverage failed");
    }
    catch { toast.error("Nem sikerült betölteni az áttekintést."); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load, normalizedMode]);
  useAdminUsageRefresh(load);
  if (busy || !data) return <div className="px-6 py-12 text-center text-[#a1a1a1] text-sm">Betöltés…</div>;
  const estimatedMode = normalizedMode === "estimated";
  const todayUsd = estimatedMode ? data.today_cost : data.today_vf_cost;
  const monthUsd = estimatedMode ? data.month_cost : data.month_vf_cost;
  const todayEcu = estimatedMode ? data.today_estimated_ecu : data.today_ecu;
  const monthEcu = estimatedMode ? data.month_estimated_ecu : data.month_ecu;
  const todayMxc = estimatedMode ? data.today_mex_credit : data.today_vf_mex_credit;
  const monthMxc = estimatedMode ? data.month_mex_credit : data.month_vf_mex_credit;
  const avgPerUser = estimatedMode ? data.avg_monthly_cost_per_user : data.avg_monthly_vf_cost_per_user;
  return (
    <div className="space-y-6" data-testid="usage-overview">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between" data-testid="usage-overview-toolbar">
        <div className="rounded-2xl border border-white/[0.06] px-4 py-3" style={{ background: "rgba(20,20,26,0.55)" }} data-testid="overview-current-calibration-card">
          <div className="text-[10px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-1">Aktuális kalibráció</div>
          <div className="text-[16px] font-semibold text-[#ffdb5c]" data-testid="overview-current-calibration-factor">{fmtFactor(data.current_calibration_factor)}</div>
          <div className="text-[11px] text-white/45 mt-1" data-testid="overview-current-calibration-meta">
            {data.current_calibration_days || 0} kalibrált nap · kizárt 0-becsült napok: {data.current_calibration_excluded_zero_estimate_days || 0}
          </div>
        </div>
        <OverviewModeSwitch mode={normalizedMode} onChange={onModeChange} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(360px,1.25fr)_1fr_1fr] gap-4" data-testid="usage-overview-top-strip">
        <YesterdayCalibrationCard data={data.yesterday_calibration} />
        <MiniStat label="Mai ECU" value={fmtEcu(todayEcu)} accent="#a294ff" dataTestId="overview-today-ecu" note={data.today_is_estimated ? "Becsült" : null} />
        <MiniStat label="Havi ECU" value={fmtEcu(monthEcu)} accent="#a294ff" dataTestId="overview-month-ecu" note={data.month_has_uncalibrated_days ? "Részben becsült" : null} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <Card label="Aktív tesztelők" value={data.active_users} accent="#5e50f7" />
        <Card label="Mai generálások" value={data.today_generations} small />
        <Card label="Havi generálások" value={data.month_generations} small />
        <Card label="Legtöbb használat" value={labelAgent(data.top_agent_month)} small />
        <MiniStat label="Mai USD" value={fmtUsd(todayUsd)} accent="#75dff5" note={data.today_is_estimated ? "Becsült" : null} />
        <MiniStat label="Havi USD" value={fmtUsd(monthUsd)} accent="#75dff5" note={data.month_has_uncalibrated_days ? "Részben becsült" : null} />
        <MiniStat label="Mai MXC" value={`${fmtMex(todayMxc)} MXC`} accent="#ffdb5c" note={data.today_is_estimated ? "Becsült" : null} />
        <MiniStat label="Havi MXC" value={`${fmtMex(monthMxc)} MXC`} accent="#ffdb5c" note={data.month_has_uncalibrated_days ? "Részben becsült" : null} />
        <MiniStat label="Átlag / aktív user / hó" value={fmtUsd(avgPerUser)} accent="#75dff5" />
        <ProgressCard
          label="Globális napi plafon"
          used={todayUsd} cap={data.global_daily_cost_usd}
          pct={data.global_daily_cost_usd ? ((todayUsd || 0) / data.global_daily_cost_usd) * 100 : 0}
        />
      </div>

      {coverage && <TopWorkflowsBlock coverage={coverage} mode={normalizedMode} />}
    </div>
  );
}

/* Sprint 3.22 v5 — ténylegesen használt workflow-k dinamikus blokkja */
function TopWorkflowsBlock({ coverage, mode }) {
  const cards = [...(coverage?.coverage || [])]
    .filter((wf) => (wf.full_run_count || 0) > 0 || (wf.partial_run_count || 0) > 0 || (wf.total_vf_usd_30d || 0) > 0 || (wf.total_usd_30d || 0) > 0)
    .sort((a, b) => {
      const am = getWorkflowMetric(a, mode)?.primaryMxc || 0;
      const bm = getWorkflowMetric(b, mode)?.primaryMxc || 0;
      return bm - am || (b.hits_30d || 0) - (a.hits_30d || 0) || ((b.last_at || "").localeCompare(a.last_at || ""));
    });

  return (
    <div data-testid="top-workflows-block">
      <div className="flex items-baseline gap-3 mb-3">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a]">Használt workflow-k · utolsó 30 nap</div>
        <div className="text-[10px] text-[#7c7c8a]">Valós nézetben exact vagy rolling faktorral korrigált full-run átlag látszik, különben raw.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {cards.map((wf) => {
          const dst = WORKFLOW_DATA_STATUS_LABEL[wf.data_status] || WORKFLOW_DATA_STATUS_LABEL.no_data;
          const metric = getWorkflowMetric(wf, mode);
          const hasMetric = Boolean(metric) && (metric.primaryMxc > 0 || metric.secondaryUsd > 0 || metric.secondaryEcu > 0);
          return (
            <div key={wf.slug} data-testid={`top-wf-${wf.slug}`} className="rounded-2xl px-4 py-4 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.5)" }}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10.5px] tracking-[0.14em] uppercase text-[#7c7c8a]">{wf.main_card}</div>
                <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: `${dst.color}18`, color: dst.color }}>
                  {dst.label}
                </span>
              </div>
              <div className="text-[15px] font-semibold text-[#c8c8d2] mb-2">{wf.sub_card}</div>
              {hasMetric ? (
                <>
                  <div className="text-[27px] font-bold tracking-tight leading-tight" style={{ color: "#ffdb5c" }} data-testid={`top-wf-primary-mxc-${wf.slug}`}>
                    {fmtMex(metric.primaryMxc)} <span className="text-[11px] font-medium opacity-60">MXC</span>
                  </div>
                  <div className="text-[12px] mt-1.5 font-medium flex items-center gap-2" data-testid={`top-wf-secondary-${wf.slug}`}>
                    <span style={{ color: "#a294ff" }}>{fmtEcu(metric.secondaryEcu)} ECU</span>
                    <span className="text-white/35">·</span>
                    <span style={{ color: "#75dff5" }}>{fmtUsd(metric.secondaryUsd)}</span>
                  </div>
                  <div className="text-[10px] text-white/45 mt-1.5" data-testid={`top-wf-helper-${wf.slug}`}>{metric.helperLabel}</div>
                {wf.is_estimated_only && mode === "calibrated" && (
                  <div className="mt-2"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: "rgba(251,191,36,0.12)", color: "#fbbf24" }}>Becsült</span></div>
                )}
                  <div className="mt-3 pt-3 border-t border-white/[0.05] flex items-center justify-between text-[10.5px]" data-testid={`top-wf-meta-${wf.slug}`}>
                    <div className="text-white/55">Teljes futások: <b className="text-[#c8c8d2]">{wf.full_run_count}</b></div>
                    <div className="text-right text-white/55">Részleges: <span className="text-[#c8c8d2]">{wf.partial_run_count}</span></div>
                  </div>
                </>
              ) : (
                <div className="text-[13px] text-[#a1a1a1] italic mt-1">
                  {wf.data_status === "partial_data" ? "Csak részadat — nincs teljes futás átlaghoz" : "Nincs elég adat"}
                </div>
              )}
              <div className="text-[10.5px] text-[#7c7c8a] mt-2 pt-2 border-t border-white/[0.04] flex items-center justify-between gap-3">
                <span>Költségforrás: <b className="text-[#c8c8d2]">{wf.cost_source}</b></span>
                <span className="text-right">{wf.last_at ? fmtTs(wf.last_at) : "—"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Sprint 3.8: session_id prefix → magyar step label (F3 párhuzamos blokkokra)
const SESSION_LABEL_PATTERNS = [
  { re: /^voice-a-/, label: "Identitás" },
  { re: /^voice-b-/, label: "Stílus" },
  { re: /^voice-c-/, label: "Összegzés" },
  { re: /^avatar-v2-a-/i,  label: "Keret" },
  { re: /^avatar-v2-b-/i,  label: "Mély" },
  { re: /^avatar-v2-c-/i,  label: "Megértés" },
  { re: /^avatar-v2-19-/,  label: "Szintézis" },
  { re: /^avatar-v2-20-/,  label: "Forrásminőség" },
  { re: /^avatar-v2-(\d+)-/, label: (m) => `Szekció ${m[1]}` },
  { re: /^offer-a-/, label: "Program mag" },
  { re: /^offer-b-/, label: "Ajánlat mechanika" },
];
function labelForStep(step, idx) {
  const sid = step.session_id || "";
  for (const p of SESSION_LABEL_PATTERNS) {
    const m = sid.match(p.re);
    if (m) return typeof p.label === "function" ? p.label(m) : p.label;
  }
  if (step.label && !step.label.startsWith("AI-hívás")) return step.label;
  return `AI-hívás ${idx + 1}`;
}

/* Ha egy F3 flow 6+ AI-hívást tartalmaz (pl. Avatar V2 ~23 hívás), csoportosítjuk
   a label-eken keresztül. Egyforma labelű hívásokat összegzünk "{label} ({N})"
   formában. Az ismeretlen "AI-hívás N" / "Szekció N" labeleket "Kiegészítő
   AI-hívások" csoportba tesszük. */
function collapseBreakdown(rows) {
  if (!rows || rows.length <= 5) return rows;
  const grouped = new Map();
  const order = [];
  rows.forEach((r) => {
    let key = r.label;
    if (/^Szekció\s+\d+/.test(key) || /^AI-hívás\s+\d+/.test(key)) key = "Kiegészítő AI-hívások";
    if (!grouped.has(key)) {
      grouped.set(key, { label: key, usd: 0, mex: 0, count: 0 });
      order.push(key);
    }
    const g = grouped.get(key);
    g.usd += r.usd || 0;
    g.mex += r.mex || 0;
    g.count += 1;
  });
  return order.map((k) => {
    const g = grouped.get(k);
    return {
      label: g.count > 1 ? `${g.label} (${g.count})` : g.label,
      usd: g.usd,
      mex: g.mex,
    };
  });
}

function ProcessCostsBlock({ costs, flows }) {
  const flowByAgent = Object.fromEntries((flows?.agents || []).map((a) => [a.agent_name, a]));
  const postByPlatform = Object.fromEntries((costs?.posts || []).map((p) => [p.platform, p]));

  // ───── Fő kártya értékei ─────
  const f3Cards = [
    { key: "brand_voice", title: "Márkahang" },
    { key: "avatar",      title: "Avatar" },
    { key: "offer",       title: "Ajánlat" },
  ].map((c) => {
    const f = flowByAgent[c.key];
    if (!f || f.flow_count === 0) {
      return { ...c, status: "no_data", usd: null, mex: null };
    }
    // Sprint 3.9: egyetlen source of truth — az UTOLSÓ tényleges flow összköltsége.
    // Így a fő kártya = Összesen sor = bontás sorainak összege, minden pixel egyezik.
    return {
      ...c,
      status: "ok",
      usd: f.last_flow_usd ?? 0,
      mex: f.last_flow_mex ?? 0,
      breakdown: f.last_flow_breakdown || [],
    };
  });
  const postCards = ["post-facebook", "post-linkedin"].map((k) => {
    const p = postByPlatform[k];
    const title = { "post-facebook": "Facebook poszt", "post-linkedin": "LinkedIn poszt" }[k];
    if (!p) return { platform: k, title, status: "no_data" };
    if (!p.has_full_flow) return { platform: k, title, status: "partial", steps: p.steps, present: p.steps_present || [], missing: p.steps_missing || [], hasRefine: p.has_refine };
    return { platform: k, title, status: "ok", usd: p.avg_full_post_usd, mex: p.avg_full_post_mex, steps: p.steps, hasRefine: p.has_refine };
  });

  return (
    <div className="space-y-8" data-testid="process-costs-block">
      {/* ═══════ FŐ KÁRTYÁK — minimalista ═══════ */}
      <div>
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-3">Folyamatköltségek</div>
        <div className="grid grid-cols-3 gap-3 mb-3" data-testid="flow-cards-f3">
          {f3Cards.map((c) => <MinimalCostCard key={c.key} testId={`flow-card-${c.key}`} {...c} />)}
        </div>
        <div className="grid grid-cols-3 gap-3" data-testid="flow-cards-posts">
          {postCards.map((c) => <MinimalCostCard key={c.platform} testId={`post-card-${c.platform}`} {...c} />)}
        </div>
      </div>

      {/* ═══════ KÖLTSÉGBONTÁS — alul, letisztult ═══════ */}
      <div>
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-3">Költségbontás</div>
        <div className="grid grid-cols-2 gap-3">
          {f3Cards.map((c) => (
            <BreakdownPanel
              key={`bd-${c.key}`}
              testId={`breakdown-${c.key}`}
              title={c.title}
              rows={collapseBreakdown((c.breakdown || []).map((s, i) => ({ label: labelForStep(s, i), usd: s.usd, mex: s.mex })))}
              totalUsd={c.usd}
              totalMex={c.mex}
              emptyText={c.status === "no_data" ? "Nincs elég adat — teljes blueprint flow még nem azonosítható." : null}
            />
          ))}
          {postCards.map((c) => (
            <PostBreakdownPanel key={`bd-${c.platform}`} testId={`breakdown-${c.platform}`} card={c} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* Minimalista költség fő kártya. Csak cím + USD + MXC. */
function MinimalCostCard({ title, status, usd, mex, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl px-5 py-4 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.65)" }}>
      <div className="text-[13px] font-semibold text-[#a294ff] mb-2.5">{title}</div>
      {status === "ok" ? (
        <div className="flex items-baseline gap-4">
          <div className="text-[22px] font-semibold text-[#29BEDA] leading-none">${(usd ?? 0).toFixed(4)}</div>
          <div className="text-[15px] font-semibold text-[#ffdb5c] leading-none">{(mex ?? 0).toFixed(1)} MXC</div>
        </div>
      ) : (
        <div className="text-[13.5px] text-[#7c7c8a] italic">
          {status === "partial" ? "Csak részadat" : "Nincs elég adat"}
        </div>
      )}
    </div>
  );
}

/* Letisztult költségbontás panel. Bal: label, jobb: USD · MXC. Alul: Összesen. */
function BreakdownPanel({ title, rows, totalUsd, totalMex, emptyText, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl px-4 py-3.5 border border-white/[0.05]" style={{ background: "rgba(15,15,22,0.6)" }}>
      <div className="text-[12.5px] font-semibold text-[#c8c8d2] mb-2.5">{title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-[11.5px] text-[#7c7c8a] italic">{emptyText || "Nincs elég adat"}</div>
      ) : (
        <>
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-1 text-[11.5px]">
              <div className="text-[#a1a1a1]">{r.label}</div>
              <div className="flex items-baseline gap-3">
                <span className="text-[#29BEDA] tabular-nums">${(r.usd ?? 0).toFixed(4)}</span>
                <span className="text-[#ffdb5c] tabular-nums min-w-[52px] text-right">{(r.mex ?? 0).toFixed(1)} MXC</span>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 mt-1.5 border-t border-white/[0.06] text-[12px]">
            <div className="font-semibold text-[#c8c8d2]">Összesen</div>
            <div className="flex items-baseline gap-3">
              <span className="text-[#29BEDA] font-semibold tabular-nums">${(totalUsd ?? 0).toFixed(4)}</span>
              <span className="text-[#ffdb5c] font-semibold tabular-nums min-w-[52px] text-right">{(totalMex ?? 0).toFixed(1)} MXC</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* Poszt költségbontás — lépések: témaötletek / posztvázlatok / kész poszt / finomítás */
function PostBreakdownPanel({ card, testId }) {
  const LABELS = { topic_ideas: "Témaötletek", post_outlines: "Posztvázlatok", final_post: "Kész poszt", refine: "Finomítás" };
  const stepOrder = ["topic_ideas", "post_outlines", "final_post", "refine"];
  const s = card.steps || {};
  const rows = stepOrder.map((k) => {
    const st = s[k];
    const has = st && (st.count || 0) > 0;
    return {
      label: LABELS[k],
      key: k,
      has,
      usd: has ? st.avg_usd : 0,
      mex: has ? st.avg_mex : 0,
      required: k !== "refine",
    };
  });
  if (card.status === "no_data") {
    return (
      <div data-testid={testId} className="rounded-xl px-4 py-3.5 border border-white/[0.05]" style={{ background: "rgba(15,15,22,0.6)" }}>
        <div className="text-[12.5px] font-semibold text-[#c8c8d2] mb-2.5">{card.title}</div>
        <div className="text-[11.5px] text-[#7c7c8a] italic">Nincs elég adat</div>
      </div>
    );
  }
  const totalUsd = rows.filter((r) => r.has).reduce((a, r) => a + (r.usd || 0), 0);
  const totalMex = rows.filter((r) => r.has).reduce((a, r) => a + (r.mex || 0), 0);
  return (
    <div data-testid={testId} className="rounded-xl px-4 py-3.5 border border-white/[0.05]" style={{ background: "rgba(15,15,22,0.6)" }}>
      <div className="text-[12.5px] font-semibold text-[#c8c8d2] mb-2.5">{card.title}</div>
      {rows.map((r) => (
        <div key={r.key} className="flex items-center justify-between py-1 text-[11.5px]">
          <div className={r.has ? "text-[#a1a1a1]" : "text-[#5a5a68] italic"}>{r.label}</div>
          {r.has ? (
            <div className="flex items-baseline gap-3">
              <span className="text-[#29BEDA] tabular-nums">${(r.usd || 0).toFixed(4)}</span>
              <span className="text-[#ffdb5c] tabular-nums min-w-[52px] text-right">{(r.mex || 0).toFixed(1)} MXC</span>
            </div>
          ) : (
            <span className="text-[#5a5a68] italic">{r.required ? "hiányzik" : "—"}</span>
          )}
        </div>
      ))}
      <div className="flex items-center justify-between pt-2 mt-1.5 border-t border-white/[0.06] text-[12px]">
        <div className="font-semibold text-[#c8c8d2]">Összesen</div>
        <div className="flex items-baseline gap-3">
          <span className="text-[#29BEDA] font-semibold tabular-nums">${totalUsd.toFixed(4)}</span>
          <span className="text-[#ffdb5c] font-semibold tabular-nums min-w-[52px] text-right">{totalMex.toFixed(1)} MXC</span>
        </div>
      </div>
      {card.status === "partial" && (
        <div className="text-[10.5px] text-[#7c7c8a] mt-2 italic">Csak részadat — a teljes posztfolyamat még nem futott le.</div>
      )}
    </div>
  );
}

function Card({ label, value, accent = "#e4e4ec", small = false }) {
  return (
    <div className="rounded-2xl px-5 py-4 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.6)" }}>
      <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-1.5">{label}</div>
      <div className={small ? "text-[15px] font-medium" : "text-[26px] font-semibold"} style={{ color: accent }}>{value}</div>
    </div>
  );
}

function YesterdayCalibrationCard({ data }) {
  const sourceMeta = getFactorSourceMeta(data?.factor_source);
  return (
    <div className="rounded-2xl px-5 py-4 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.6)" }} data-testid="universal-key-balance-card">
      <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-1.5">Tegnapi ECU kalibráció</div>
      <div className="text-[23px] font-semibold text-white mb-3" data-testid="yesterday-calibration-date">{data?.date || "—"}</div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.08em] text-white/45">Becsült</div>
          <div className="text-[15px] font-semibold text-[#c8c8d2]" data-testid="yesterday-estimated-ecu">{fmtEcu(data?.estimated_total_ecu)} ECU</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.08em] text-white/45">Tényleges</div>
          <div className="text-[15px] font-semibold text-[#a294ff]" data-testid="yesterday-actual-ecu">{data?.actual_app_ecu == null ? "—" : `${fmtEcu(data.actual_app_ecu)} ECU`}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.08em] text-white/45">Faktor</div>
          <div className="text-[15px] font-semibold text-[#ffdb5c]" data-testid="yesterday-factor">{data?.correction_factor == null ? "—" : fmtFactor(data.correction_factor)}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 text-[10.5px] text-[#8a8a99]">
        <span>Forrás:</span>
        <span style={{ color: sourceMeta.color }} data-testid="yesterday-factor-source">{data?.status_label || sourceMeta.label}</span>
      </div>
    </div>
  );
}

function ProgressCard({ label, used, cap, pct }) {
  const color = pct >= 100 ? "#fb7185" : pct >= 80 ? "#fbbf24" : "#22c55e";
  return (
    <div className="rounded-2xl px-5 py-4 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.6)" }}>
      <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-1.5">{label}</div>
      <div className="text-[20px] font-semibold mb-2" style={{ color }}>${(used ?? 0).toFixed(2)} / ${(cap ?? 0).toFixed(2)}</div>
      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div style={{ width: `${Math.min(pct, 100)}%`, background: color, height: "100%" }} />
      </div>
    </div>
  );
}

function RawVfMxUsdStat({ label, rawUsd, rawMxc, vfUsd, vfMxc, testId, vfAccent = "#ffdb5c" }) {
  return (
    <div className="rounded-2xl px-5 py-4 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.6)" }} data-testid={testId}>
      <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-2">{label}</div>
      <div className="text-[26px] font-bold tracking-tight leading-tight" style={{ color: vfAccent }}>
        {fmtMex(vfMxc)} <span className="text-[11px] font-medium opacity-60">MXC</span>
      </div>
      <div className="text-[12px] mt-1.5 font-medium" style={{ color: "#75dff5" }}>{fmtUsd(vfUsd)}</div>
      <div className="text-[10.5px] mt-3 pt-3 border-t border-white/[0.05] text-white/60">Belső: {fmtUsd(rawUsd)} · {fmtMex(rawMxc)} MXC</div>
    </div>
  );
}

function RawVfCostCell({ rawUsd, rawMxc, vfUsd, vfMxc, vfColor = "#ffdb5c", align = "right", testId }) {
  return (
    <div className={`leading-tight ${align === "right" ? "text-right" : "text-left"}`} data-testid={testId}>
      <div className="text-[14.5px] font-semibold text-white">{fmtUsd4(vfUsd)}</div>
      <div className="text-[10.5px] mt-0.5 font-medium" style={{ color: vfColor }}>
        {fmtMex(vfMxc)} MXC
      </div>
      <div className="text-[10px] mt-1.5 text-white/55">Belső: {fmtUsd(rawUsd)} · {fmtMex(rawMxc)} MXC</div>
    </div>
  );
}

function WorkflowCoverageMetricCell({ mxc, ecu, usd, testId, emptyText = "—" }) {
  const hasValue = (mxc || 0) > 0 || (ecu || 0) > 0 || (usd || 0) > 0;
  return (
    <div className="leading-tight text-right" data-testid={testId}>
      {hasValue ? (
        <>
          <div className="text-[14px] font-semibold text-[#ffdb5c]">{fmtMex(mxc)} MXC</div>
          <div className="text-[10.5px] mt-0.5 text-white/70">
            <span className="text-[#a294ff]">{fmtEcu(ecu)} ECU</span>
            <span className="text-white/30"> · </span>
            <span className="text-[#75dff5]">{fmtUsd(usd)}</span>
          </div>
        </>
      ) : (
        <div className="text-[11.5px] text-[#7c7c8a] italic">{emptyText}</div>
      )}
    </div>
  );
}

function CorrectionFactorChip({ factor, source, testId }) {
  const meta = FACTOR_SOURCE_META[source] || FACTOR_SOURCE_META.raw_1x;
  const color = meta.color;
  const label = `${fmtFactor(factor)} · ${meta.label}`;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
      style={{ background: `${color}18`, color }}
      data-testid={testId}
      title={source === "exact" ? "Hivatalos napi actual ECU alapján számolt exact faktor." : source === "rolling" ? "Az előző lezárt valid actual napok súlyozott rolling faktora." : source === "pending" ? "Official reconciliation pending — erre a napra még nincs hivatalos actual ECU." : "Nincs valid actual ECU, raw 1.0x marad."}
    >
      {label}
    </span>
  );
}

function UsageLogWorkflowCell({ log }) {
  return (
    <div className="min-w-0 leading-tight" data-testid={`usage-log-workflow-${log.id}`}>
      <div className="text-[#c8c8d2] font-medium truncate">{labelAgent(log.agent_name)}</div>
      <div className="text-[10px] text-[#a294ff] truncate mt-0.5">
        {labelSub(log.sub_agent)} · {labelReq(log.request_type)}
      </div>
    </div>
  );
}

function UsageLogTechnicalCell({ log }) {
  const inputTokens = log.input_tokens ?? log.estimated_input_tokens;
  const outputTokens = log.output_tokens ?? log.estimated_output_tokens;
  return (
    <div className="min-w-0 leading-tight text-right" data-testid={`usage-log-technical-${log.id}`}>
      <div className="text-[10px] text-[#c8c8d2] truncate">{log.model_name || log.model || "—"}</div>
      <div className="text-[9.5px] text-[#7c7c8a] mt-0.5">IN {inputTokens ?? "—"} · OUT {outputTokens ?? "—"}</div>
      <div className="text-[9.5px] text-[#7c7c8a] mt-0.5">{log.duration_ms ?? "—"} ms</div>
    </div>
  );
}

function UsageLogEstimatedEcuCell({ log, mode = "calibrated" }) {
  const display = getUsageLogDisplay(log, mode);
  return (
    <div className="text-right leading-tight" data-testid={`usage-log-estimated-ecu-${log.id}`}>
      <div className="text-[11px] text-[#c8c8d2]">{fmtEcu(display.ecu)} ECU</div>
    </div>
  );
}

function UsageLogCalibratedEcuCell({ log, mode = "calibrated" }) {
  const display = getUsageLogDisplay(log, mode);
  const meta = FACTOR_SOURCE_META[display.source] || FACTOR_SOURCE_META.raw_1x;
  return (
    <div className="text-right leading-tight" data-testid={`usage-log-calibrated-ecu-${log.id}`}>
      <div className="text-[12px] font-semibold" style={{ color: meta.color }}>
        {fmtFactor(display.factor)}
      </div>
      <div className="text-[9.5px] mt-0.5" style={{ color: meta.color }}>
        {meta.label}
      </div>
    </div>
  );
}

function UsageLogUsdCell({ log, mode = "calibrated" }) {
  const display = getUsageLogDisplay(log, mode);
  return (
    <div className="text-right leading-tight" data-testid={`usage-log-usd-${log.id}`}>
      <div className="text-[12px] font-semibold text-[#75dff5]">{fmtUsd(display.usd)}</div>
    </div>
  );
}

function UsageLogMxcCell({ log, mode = "calibrated" }) {
  const display = getUsageLogDisplay(log, mode);
  return (
    <div className="text-right leading-tight" data-testid={`usage-log-mxc-${log.id}`}>
      <div className="text-[12px] font-semibold text-[#ffdb5c]">{fmtMex(display.mxc)} MXC</div>
    </div>
  );
}

/* ─────────── Rendezhető fejléc helper ─────────── */
function SortHeader({ label, active, dir, onClick, align = "left" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] hover:text-white/80 focus:outline-none"
      style={{ justifyContent: align === "right" ? "flex-end" : "flex-start", width: "100%" }}
      data-testid={`sort-${label.toLowerCase().replace(/[^a-z0-9]/g,"-")}`}
    >
      <span className={active ? "text-white/90" : ""}>{label}</span>
      {active
        ? (dir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)
        : <CaretUpDown size={11} weight="regular" opacity={0.5} />
      }
    </button>
  );
}

function useSort(rows, defaultKey, defaultDir = "desc") {
  const [key, setKey] = useState(defaultKey);
  const [dir, setDir] = useState(defaultDir);
  const toggle = (k) => {
    if (k === key) { setDir((d) => d === "asc" ? "desc" : "asc"); }
    else { setKey(k); setDir("asc"); }
  };
  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = key.includes(".") ? key.split(".").reduce((o, s) => o?.[s], a) : a[key];
      const bv = key.includes(".") ? key.split(".").reduce((o, s) => o?.[s], b) : b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return dir === "asc" ? av - bv : bv - av;
      const as = String(av).toLowerCase(), bs = String(bv).toLowerCase();
      return dir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return copy;
  }, [rows, key, dir]);
  return { sorted, key, dir, toggle, setKey, setDir };
}

/* ─────────── Users usage table ─────────── */
export function UsersUsageTab({ mode = "calibrated" }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [drillEmail, setDrillEmail] = useState(null);
  const [dailyCapEditFor, setDailyCapEditFor] = useState(null);
  const [monthlyCapEditFor, setMonthlyCapEditFor] = useState(null);
  const [busyRow, setBusyRow] = useState(null);
  const [search, setSearch] = useState("");
  const normalizedMode = normalizeAdminUsageMode(mode);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/usage/users"); setRows(r.data.users || []); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load, normalizedMode]);
  useAdminUsageRefresh(load);

  const { sorted, key, dir, toggle, setKey, setDir } = useSort(rows, normalizedMode === "estimated" ? "month_mex_credit" : "month_vf_mex_credit", "desc");

  useEffect(() => {
    setKey(normalizedMode === "estimated" ? "month_mex_credit" : "month_vf_mex_credit");
    setDir("desc");
  }, [normalizedMode, setKey, setDir]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((r) =>
      (r.email || "").toLowerCase().includes(q) ||
      (r.name || "").toLowerCase().includes(q)
    );
  }, [sorted, search]);

  const quickAddMxc = async (email, amount) => {
    if (busyRow) return;
    setBusyRow(email);
    try {
      const r = await api.post(`/admin/users/${encodeURIComponent(email)}/mxc/quick-add`, { amount, note: `Gyors +${amount} MXC` });
      toast.success(`+${amount} MXC (≈${fmtUsd(r.data.amount_added_usd)}) hozzáadva ${email}-nak.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hozzáadás sikertelen.");
    } finally { setBusyRow(null); }
  };

  if (busy) return <div className="px-6 py-12 text-center text-[#a1a1a1] text-sm">Betöltés…</div>;

  // Sprint 3.22 v3 — Kompaktabb + levegősebb + stabil sticky.
  // Oszlopok: Név(sticky) · Havi keret · Mai · Havi fogyás · Maradék · Napi limit · Aktivitás · Utolsó · Státusz · Gyors
  const cols = [
    { w: 260, key: "name", label: "Név / Email", align: "left", sticky: true },
    { w: 140, key: "effective_monthly_mxc", label: "Havi keret", align: "right" },
    { w: 185, key: "today_vf_mex_credit", label: "Mai fogyás", align: "right" },
    { w: 190, key: "month_vf_mex_credit", label: "Havi fogyás", align: "right" },
    { w: 140, key: "remaining_monthly_mxc", label: "Maradék havi", align: "right" },
    { w: 130, key: "daily_cap_mxc", label: "Napi limit", align: "right" },
    { w: 110, key: "month_generations", label: "Aktivitás", align: "right" },
    { w: 130, key: "last_login_at", label: "Utolsó akt.", align: "left" },
    { w: 100, key: "status", label: "Státusz", align: "left" },
    { w: 210, key: null, label: "Gyors műveletek", align: "left" },
  ];
  const totalW = cols.reduce((s, c) => s + c.w, 0) + (cols.length - 1) * 12;
  const gridTemplate = cols.map((c) => `${c.w}px`).join(" ");
  const stickyShadow = "6px 0 12px -8px rgba(0,0,0,0.7)";

  return (
    <>
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <h3 className="font-heading text-[18px] font-semibold">Tesztelők / Hozzáférések</h3>
          <p className="text-[12px] text-[#a1a1a1] mt-0.5">MXC az elsődleges adat, USD másodlagos. A fogyás itt {normalizedMode === "estimated" ? "raw becslésként" : "valós, ECU-alapú korrigált értékként"} jelenik meg.</p>
          <p className="text-[11px] text-[#8a8a99] mt-1.5" data-testid="users-usage-monthly-cap-help">User-specifikus havi kerethez a sor végén a <b>Havi MXC</b> gombot használd. Ez csak az adott userre ment override-ot.</p>
        </div>
        <div className="relative shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés név vagy email alapján…"
            data-testid="users-usage-search"
            className="w-[260px] pl-4 pr-9 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13px] text-white focus:outline-none focus:border-[#5e50f7]"
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} data-testid="users-usage-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/[0.08] text-[#7c7c8a]">
              <X size={13} />
            </button>
          )}
        </div>
      </div>
      <div className="rounded-2xl border border-white/[0.06] overflow-hidden" style={{ background: "rgba(20,20,26,0.6)" }} data-testid="users-usage-table">
        <div className="overflow-x-auto">
          <div style={{ minWidth: `${totalW}px`, position: "relative" }}>
            {/* Header */}
            <div style={{ display: "grid", gridTemplateColumns: gridTemplate, gap: 12 }} className="px-5 py-3.5 border-b border-white/[0.06] items-center bg-[#14141a]">
              {cols.map((c) => (
                <div
                  key={c.label}
                  style={c.sticky ? { position: "sticky", left: 0, zIndex: 3, background: "#14141a", height: "100%", display: "flex", alignItems: "center", boxShadow: stickyShadow, marginRight: -12, paddingRight: 12 } : undefined}
                >
                  {c.key ? (
                    <SortHeader label={c.label} active={key===c.key} dir={dir} onClick={() => toggle(c.key)} align={c.align === "right" ? "right" : "left"} />
                  ) : (
                    <div className="text-[10.5px] tracking-[0.14em] uppercase text-[#7c7c8a]">{c.label}</div>
                  )}
                </div>
              ))}
            </div>
            {/* Rows */}
            {filtered.map((r, idx) => {
              const isAdmin = r.role === "admin";
              const display = getUserUsageDisplay(r, normalizedMode);
              const remainColor = isAdmin ? "#a294ff" : (display.remainingMxc || 0) <= 0 ? "#fb7185" : "#4ade80";
              const monthColor = getStatusColor(display.monthStatus, isAdmin);
              const dailyColor = getStatusColor(display.dayStatus, isAdmin);
              const stickyBg = idx % 2 === 0 ? "#14141a" : "#171720";
              return (
                <div
                  key={r.email}
                  data-testid={`user-row-${r.email}`}
                  style={{ display: "grid", gridTemplateColumns: gridTemplate, gap: 12, background: stickyBg }}
                  className="px-5 py-4 items-center border-b border-white/[0.04] text-[13px] hover:bg-white/[0.03] transition"
                >
                  {/* NÉV / EMAIL — sticky */}
                  <button
                    type="button"
                    onClick={() => setDrillEmail(r.email)}
                    data-testid={`user-drill-open-${r.email}`}
                    style={{ position: "sticky", left: 0, zIndex: 2, background: stickyBg, height: "100%", boxShadow: stickyShadow, marginRight: -12, paddingRight: 12, display: "flex", flexDirection: "column", justifyContent: "center" }}
                    className="truncate min-w-0 text-left"
                  >
                    <div className="font-medium truncate flex items-center gap-1.5">
                      {r.name || r.email.split("@")[0]}
                      {isAdmin && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider" style={{ background: "rgba(94,80,247,0.18)", color: "#a294ff" }}>ADMIN</span>}
                      {!isAdmin && r.credit_plan_label && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider" style={{ background: "rgba(255,219,92,0.16)", color: "#ffdb5c" }}>{r.credit_plan_label}</span>}
                    </div>
                    <div className="text-[#a1a1a1] text-[11.5px] truncate mt-0.5">{r.email}</div>
                  </button>
                  {/* HAVI KERET — MXC primary, USD secondary */}
                  <MxUsdCell
                    isAdmin={isAdmin}
                    mxc={r.effective_monthly_mxc}
                    usd={r.effective_monthly_usd}
                    mxColor="#ffdb5c"
                    bonusHint={(r.bonus_usd_allowance ?? 0) > 0 ? `+${fmtMex(r.extra_mex_credit)} bónusz` : null}
                  />
                  {/* MAI FOGYÁS */}
                  <MxUsdCell isAdmin={isAdmin} mxc={display.todayUsedMxc} usd={display.todayUsedUsd} mxColor={dailyColor} />
                  {/* HAVI FOGYÁS */}
                  <MxUsdCell isAdmin={isAdmin} mxc={display.monthUsedMxc} usd={display.monthUsedUsd} mxColor={monthColor} bold />
                  {/* MARADÉK HAVI */}
                  <MxUsdCell isAdmin={isAdmin} mxc={display.remainingMxc} usd={display.remainingUsd} mxColor={remainColor} bold />
                  {/* NAPI LIMIT */}
                  <MxUsdCell isAdmin={isAdmin} mxc={r.daily_cap_mxc} usd={r.daily_cap_usd} mxColor="#ffdb5c" />
                  {/* AKTIVITÁS — másodlagos */}
                  <div className="text-right">
                    <div className="text-[12px] text-[#c8c8d2]">{r.month_generations ?? 0}</div>
                    <div className="text-[10px] text-[#7c7c8a]">ma: {r.today_generations ?? 0}</div>
                  </div>
                  {/* UTOLSÓ */}
                  <div className="text-[11.5px] text-[#a1a1a1]">{fmtTs(r.last_login_at)}</div>
                  {/* STÁTUSZ */}
                  <div className="flex flex-col gap-1">
                    <StatusChip status={r.status} />
                    {r.has_limit_override && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold self-start cursor-help"
                        style={{ background: "rgba(162,148,255,0.14)", color: "#a294ff", border: "1px solid rgba(162,148,255,0.25)" }}
                        title="Ennek a usernek egyedi limitje vagy bónusz MXC-je van"
                        data-testid={`limit-override-badge-${r.email}`}
                      >
                        Egyedi
                      </span>
                    )}
                  </div>
                  {/* GYORS MŰVELETEK */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <button
                      disabled={busyRow === r.email || isAdmin}
                      onClick={(e) => { e.stopPropagation(); quickAddMxc(r.email, 50); }}
                      data-testid={`row-add-mxc-50-${r.email}`}
                      className="px-2 py-1 rounded-md text-[11px] font-semibold bg-[#ffdb5c22] hover:bg-[#ffdb5c33] text-[#ffdb5c] disabled:opacity-30 disabled:cursor-not-allowed"
                      title={isAdmin ? "Adminra nem alkalmazható" : "+50 MXC (≈ +$1 belső keret)"}
                    >
                      +50
                    </button>
                    <button
                      disabled={busyRow === r.email || isAdmin}
                      onClick={(e) => { e.stopPropagation(); quickAddMxc(r.email, 100); }}
                      data-testid={`row-add-mxc-100-${r.email}`}
                      className="px-2 py-1 rounded-md text-[11px] font-semibold bg-[#ffdb5c22] hover:bg-[#ffdb5c33] text-[#ffdb5c] disabled:opacity-30 disabled:cursor-not-allowed"
                      title={isAdmin ? "Adminra nem alkalmazható" : "+100 MXC (≈ +$2 belső keret)"}
                    >
                      +100
                    </button>
                    <button
                      disabled={isAdmin}
                      onClick={(e) => { e.stopPropagation(); setDailyCapEditFor(r); }}
                      data-testid={`row-edit-daily-cap-${r.email}`}
                      className="px-2 py-1 rounded-md text-[11px] font-semibold bg-white/[0.06] hover:bg-white/[0.12] text-[#c8c8d2] disabled:opacity-30 disabled:cursor-not-allowed"
                      title={isAdmin ? "Adminra nem alkalmazható" : "Napi USD limit módosítása"}
                    >
                      Napi
                    </button>
                    <button
                      disabled={isAdmin}
                      onClick={(e) => { e.stopPropagation(); setMonthlyCapEditFor(r); }}
                      data-testid={`row-edit-monthly-cap-${r.email}`}
                      className="px-2 py-1 rounded-md text-[11px] font-semibold bg-white/[0.06] hover:bg-white/[0.12] text-[#c8c8d2] disabled:opacity-30 disabled:cursor-not-allowed"
                      title={isAdmin ? "Adminra nem alkalmazható" : "Egyedi havi MXC keret módosítása"}
                    >
                      Havi MXC
                    </button>
                  </div>
                </div>
              );
            })}
            {sorted.length === 0 && <div className="px-5 py-10 text-center text-[#a1a1a1] text-sm">Még nincs usage adat.</div>}
            {sorted.length > 0 && filtered.length === 0 && <div className="px-5 py-10 text-center text-[#a1a1a1] text-sm" data-testid="users-usage-no-match">Nincs találat a keresésre: „{search}".</div>}
          </div>
        </div>
      </div>
      {drillEmail && <UserDrillDown email={drillEmail} onClose={() => setDrillEmail(null)} onDataChange={load} />}
      {dailyCapEditFor && (
        <CapQuickEdit
          row={dailyCapEditFor}
          onClose={() => setDailyCapEditFor(null)}
          onSaved={() => { setDailyCapEditFor(null); load(); }}
          scope="daily"
        />
      )}
      {monthlyCapEditFor && (
        <CapQuickEdit
          row={monthlyCapEditFor}
          onClose={() => setMonthlyCapEditFor(null)}
          onSaved={() => { setMonthlyCapEditFor(null); load(); }}
          scope="monthly"
        />
      )}
    </>
  );
}

/* MXC primary / USD secondary cell. */
function MxUsdCell({ isAdmin, mxc, usd, mxColor = "#ffdb5c", bold = false, bonusHint = null }) {
  if (isAdmin) {
    return (
      <div className="text-right">
        <div className="text-[11px] tracking-[0.12em] uppercase text-[#a294ff]">Korlátlan</div>
      </div>
    );
  }
  return (
    <div className="text-right leading-tight">
      <div className={`text-[17px] ${bold ? "font-bold" : "font-semibold"}`} style={{ color: mxColor }}>
        {fmtMex(mxc)} <span className="text-[10px] font-medium opacity-60">MXC</span>
      </div>
      <div className="text-[11px] mt-1" style={{ color: "#75dff5" }}>{fmtUsd(usd)}</div>
      {bonusHint && <div className="text-[10px] text-[#a294ff] mt-0.5">{bonusHint}</div>}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    active: ["#4ade80", "Aktív"],
    invited: ["#75dff5", "Meghívva"],
    inactive: ["#fb7185", "Letiltva"],
    suspended: ["#fb7185", "Felfüggesztve"],
    expired: ["#fb7185", "Lejárt"],
  };
  const [c, t] = map[status] || ["#a1a1a1", status || "—"];
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold self-start"
      style={{ background: `${c}18`, color: c }}
    >
      {t}
    </span>
  );
}

/* Sprint 3.22 v2 — Egyesített napi/havi USD cap gyors szerkesztő modal */
function CapQuickEdit({ row, onClose, onSaved, scope }) {
  const isDaily = scope === "daily";
  const ovKey = isDaily ? "daily_cost_usd" : "monthly_cost_usd";
  const currentOverride = row.override_details?.[ovKey];
  const currentValue = isDaily ? row.daily_cap_usd : row.effective_monthly_usd - (row.bonus_usd_allowance || 0);
  const currentMxc = isDaily ? row.daily_cap_mxc : (row.base_monthly_mxc);
  const mexPerUsd = row.mex_per_usd || 50;
  const [value, setValue] = useState(
    isDaily
      ? (currentOverride != null ? String(currentOverride) : String(currentValue ?? 2.20))
      : (currentOverride != null ? String((currentOverride * mexPerUsd).toFixed(1)) : String(currentMxc ?? 350))
  );
  const [saving, setSaving] = useState(false);

  const presets = isDaily ? [1.0, 2.20, 3.0, 5.0] : [200, 350, 500, 750, 1000];
  const endpoint = isDaily ? "daily-cap" : "monthly-cap";
  const titleTxt = isDaily ? "Napi USD hard cap" : "Egyedi havi MXC keret";
  const defaultTxt = isDaily ? "$2.20" : `${fmtMex(row.base_monthly_mxc)} MXC`;

  const save = async (customVal) => {
    setSaving(true);
    try {
      const v = customVal != null ? customVal : parseFloat(value);
      if (isNaN(v) || v < 0) { toast.error(`Érvényes ${isDaily ? "USD" : "MXC"} összeg szükséges.`); setSaving(false); return; }
      const body = isDaily ? { daily_cost_usd: v } : { monthly_cost_usd: Number((v / mexPerUsd).toFixed(4)) };
      await api.patch(`/admin/users/${encodeURIComponent(row.email)}/${endpoint}`, body);
      if (isDaily) {
        toast.success(`${titleTxt}: ${fmtUsd(v)} (${fmtMex(v * mexPerUsd)} MXC)`);
      } else {
        toast.success(`${titleTxt}: ${fmtMex(v)} MXC (${fmtUsd(v / mexPerUsd)})`);
      }
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mentés sikertelen."); }
    finally { setSaving(false); }
  };
  const clearOverride = async () => {
    setSaving(true);
    try {
      const body = isDaily ? { daily_cost_usd: null } : { monthly_cost_usd: null };
      await api.patch(`/admin/users/${encodeURIComponent(row.email)}/${endpoint}`, body);
      toast.success(`Visszaáll a globális ${titleTxt.toLowerCase()}-ra.`);
      onSaved?.();
    } catch { toast.error("Törlés sikertelen."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" style={{ background: "rgba(8,8,12,0.86)", backdropFilter: "blur(8px)" }} onClick={onClose} data-testid={`${scope}-cap-quick-edit-overlay`}>
      <div className="relative w-full max-w-[460px] rounded-2xl p-6 text-white" style={{ background: "#16161f", border: "1px solid rgba(255,255,255,0.08)" }} onClick={(e) => e.stopPropagation()} data-testid={`${scope}-cap-quick-edit-modal`}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/[0.06]"><X size={14} /></button>
        <h3 className="font-heading text-[17px] font-semibold mb-1">{titleTxt}</h3>
        <p className="text-[12px] text-[#a1a1a1] mb-1">{row.name || row.email}</p>
        <p className="text-[11.5px] text-[#7c7c8a] mb-4">
          Jelenlegi: <b className="text-[#ffdb5c]">{fmtMex(currentMxc)} MXC</b> · {fmtUsd(currentValue)}
          {currentOverride != null && <span className="text-[#a294ff] ml-2">(egyedi)</span>}
        </p>
        {scope === "monthly" && (
          <div className="rounded-lg px-3 py-2 text-[11px] mb-4" style={{ background: "rgba(117,223,245,0.08)", border: "1px solid rgba(117,223,245,0.16)", color: "#b9eff8" }} data-testid="monthly-cap-separation-note">
            Ez egy <b>user-specifikus havi limit override</b>. Nem írja át a globális / csomag defaultot, csak ennek a usernek. A <b>Monthly usage</b> külön marad, és a reset sem módosítja ezt a limitet.
          </div>
        )}
        <div className="flex flex-wrap gap-2 mb-4">
          {presets.map((p) => (
            <button
              key={p}
              disabled={saving}
              onClick={() => save(p)}
              data-testid={`${scope}-cap-preset-${p}`}
              className="px-3 py-2 rounded-lg text-[13px] font-semibold bg-white/[0.05] hover:bg-white/[0.12] text-[#c8c8d2] disabled:opacity-40"
            >
              <div className="text-[#ffdb5c]">{isDaily ? `${fmtMex(p * mexPerUsd)} MXC` : `${fmtMex(p)} MXC`}</div>
              <div className="text-[10px] text-[#7c7c8a] mt-0.5">{isDaily ? fmtUsd(p) : fmtUsd(p / mexPerUsd)}</div>
            </button>
          ))}
        </div>
        <label className="block mb-4">
          <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Egyedi érték ({isDaily ? "USD" : "MXC"})</span>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              {isDaily && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7c7c8a]">$</span>}
              <input type="number" step="0.10" min="0" value={value} onChange={(e) => setValue(e.target.value)}
                data-testid={`${scope}-cap-custom-input`}
                className={`w-full ${isDaily ? "pl-7" : "pl-4"} pr-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]`} />
            </div>
            <button onClick={() => save(null)} disabled={saving} data-testid={`${scope}-cap-save-custom`}
              className="px-4 py-2.5 rounded-lg text-[13px] font-semibold text-white"
              style={{ background: "linear-gradient(135deg, #5e50f7, #4F46E5)", opacity: saving ? 0.6 : 1 }}>
              {saving ? "…" : "Mentés"}
            </button>
          </div>
          {!isNaN(parseFloat(value)) && parseFloat(value) > 0 && (
            <div className="text-[10.5px] text-[#7c7c8a] mt-1.5">≈ <b className="text-[#ffdb5c]">{isDaily ? fmtMex(parseFloat(value) * mexPerUsd) : fmtUsd(parseFloat(value) / mexPerUsd)}</b>{isDaily ? " MXC" : ""}</div>
          )}
        </label>
        {currentOverride != null && (
          <button onClick={clearOverride} disabled={saving} data-testid={`${scope}-cap-clear`}
            className="w-full px-3 py-2 rounded-lg text-[12px] font-semibold text-[#fb7185] hover:bg-[#fb718522]">
            Egyedi limit törlése (vissza a globális {defaultTxt} defaulthoz)
          </button>
        )}
      </div>
    </div>
  );
}


/* ─────────── User drill-down modal ─────────── */
function UserDrillDown({ email, onClose, onDataChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [busyAction, setBusyAction] = useState(false);
  const [showLimitsEdit, setShowLimitsEdit] = useState(false);
  const [showBonusEdit, setShowBonusEdit] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [b, u] = await Promise.all([
        api.get(`/admin/usage/users/${encodeURIComponent(email)}/breakdown`),
        api.get("/admin/usage/users"),
      ]);
      const stat = (u.data.users || []).find((x) => x.email === email);
      setData({ ...b.data, stat });
    } catch { toast.error("Nem sikerült betölteni a részleteket."); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [email]);

  const adjustCredit = async (amount) => {
    if (busyAction) return;
    setBusyAction(true);
    try {
      await api.post(`/admin/users/${encodeURIComponent(email)}/credit/adjust`, { amount, note: `admin quick +${amount}` });
      toast.success(`+${amount} MEX kredit hozzáadva.`);
      await load();
      onDataChange?.();
    } catch { toast.error("Kredit hozzáadás sikertelen."); }
    finally { setBusyAction(false); }
  };

  const resetUsage = async (scope) => {
    if (busyAction) return;
    if (!window.confirm(scope === "daily" ? "Biztosan resetled a mai használatot?" : "Biztosan resetled a havi használatot?")) return;
    setBusyAction(true);
    try {
      await api.post(`/admin/users/${encodeURIComponent(email)}/usage/reset-${scope}`);
      toast.success(`${scope === "daily" ? "Mai" : "Havi"} használat resetelve.`);
      await load();
      onDataChange?.();
    } catch { toast.error("Reset sikertelen."); }
    finally { setBusyAction(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="user-drilldown-overlay"
    >
      <div
        className="my-10 w-[92%] max-w-[1200px] rounded-2xl border border-white/[0.08] overflow-hidden"
        style={{ background: "rgba(16,16,22,0.98)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="user-drilldown-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <div>
            <h3 className="font-heading text-[22px] font-bold tracking-tight" data-testid="user-drilldown-title">
              {data?.user?.name || (data?.user?.email ? data.user.email.split("@")[0] : email.split("@")[0])}
            </h3>
            <div className="text-[11.5px] tracking-[0.14em] uppercase text-[#7c7c8a] mt-1">User részletező</div>
            <div className="text-[12.5px] text-[#a1a1a1] mt-0.5">{email}</div>
          </div>
          <button onClick={onClose} data-testid="user-drilldown-close" className="p-2 rounded-lg hover:bg-white/[0.06]"><X size={16} /></button>
        </div>
        {busy || !data ? (
          <div className="px-6 py-16 text-center text-[#a1a1a1] text-sm">Betöltés…</div>
        ) : (
          <div className="px-6 py-6 space-y-8">
            {/* SPRINT 3: Quick actions strip */}
            <section className="rounded-xl border border-white/[0.06] px-4 py-3 flex flex-wrap items-center gap-2" style={{ background: "rgba(94,80,247,0.06)" }}>
              <span className="text-[11px] tracking-[0.14em] uppercase text-[#a294ff] mr-2">Admin gyors műveletek</span>
              {[10, 50, 100].map((n) => (
                <button
                  key={n}
                  disabled={busyAction}
                  onClick={() => adjustCredit(n)}
                  data-testid={`drilldown-add-credit-${n}`}
                  className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-[#5e50f722] hover:bg-[#5e50f733] text-[#c0b5ff] disabled:opacity-40"
                >+{n} MEX</button>
              ))}
              <button
                disabled={busyAction}
                onClick={() => setShowBonusEdit(true)}
                data-testid="drilldown-edit-bonus-mxc"
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-[#ffdb5c22] hover:bg-[#ffdb5c33] text-[#ffdb5c] disabled:opacity-40"
              >Bónusz MXC…</button>
              <div className="mx-2 h-5 w-px bg-white/[0.08]" />
              <button
                disabled={busyAction}
                onClick={() => resetUsage("daily")}
                data-testid="drilldown-reset-daily"
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white/[0.04] hover:bg-white/[0.08] text-[#c8c8d2] disabled:opacity-40"
              >Reset mai használat</button>
              <button
                disabled={busyAction}
                onClick={() => resetUsage("monthly")}
                data-testid="drilldown-reset-monthly"
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white/[0.04] hover:bg-white/[0.08] text-[#c8c8d2] disabled:opacity-40"
              >Reset havi használat</button>
              <div className="mx-2 h-5 w-px bg-white/[0.08]" />
              <button
                onClick={() => setShowLimitsEdit(true)}
                data-testid="drilldown-edit-limits"
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white/[0.04] hover:bg-white/[0.08] text-[#c8c8d2]"
              >Limit szerkesztés…</button>
              {data.stat?.has_limit_override && (
                <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-semibold bg-[#a294ff22] text-[#a294ff]">
                  Egyedi beállítás aktív
                </span>
              )}
            </section>

            {/* SUMMARY — Sprint 3.22 v2: MXC elsődleges, USD másodlagos */}
            <section>
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-3">Limit szerkezet</div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <MxUsdStat label="Alap havi limit" mxc={data.stat?.base_monthly_mxc} usd={data.stat?.base_monthly_usd} accent="#ffdb5c" testId="drilldown-base-monthly-limit" />
                <MxUsdStat label="Extra / bónusz MXC" mxc={data.stat?.extra_mex_credit} usd={data.stat?.bonus_usd_allowance} accent={(data.stat?.extra_mex_credit ?? 0) > 0 ? "#a294ff" : "#7c7c8a"} testId="drilldown-extra-bonus-mxc" />
                <MxUsdStat label="Effektív havi keret" mxc={data.stat?.effective_monthly_mxc} usd={data.stat?.effective_monthly_usd} accent="#4ade80" testId="drilldown-effective-monthly-limit" />
              </div>
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-3">Havi keret és fogyás</div>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <MxUsdStat label="Effektív havi keret"      mxc={data.stat?.effective_monthly_mxc} usd={data.stat?.effective_monthly_usd} adminUnlimited={data.stat?.role === "admin"} testId="drilldown-monthly-effective-card" />
                <RawVfMxUsdStat
                  label="Havi fogyás"
                  rawMxc={data.stat?.month_mex_credit}
                  rawUsd={data.stat?.month_cost}
                  vfMxc={data.stat?.month_vf_mex_credit}
                  vfUsd={data.stat?.month_vf_cost}
                  testId="drilldown-month-raw-vf"
                />
                <MxUsdStat label="Maradék havi"    mxc={data.stat?.remaining_monthly_mxc} usd={data.stat?.remaining_monthly_usd} adminUnlimited={data.stat?.role === "admin"} accent={data.stat?.monthly_budget_status === "reached" ? "#fb7185" : data.stat?.monthly_budget_status === "near" ? "#fbbf24" : "#4ade80"} />
                <MxUsdStat label="Napi limit"      mxc={data.stat?.daily_cap_mxc}         usd={data.stat?.daily_cap_usd} adminUnlimited={data.stat?.role === "admin"} />
              </div>
              {(data.stat?.bonus_usd_allowance ?? 0) > 0 && (
                <div className="rounded-lg px-3 py-2 text-[12px] mb-3" style={{ background: "rgba(162,148,255,0.10)", border: "1px solid rgba(162,148,255,0.28)", color: "#c0b5ff" }} data-testid="drilldown-bonus-allowance-banner">
                  <b>Bónusz allowance aktív:</b> +{fmtMex(data.stat?.extra_mex_credit)} MXC (≈ +{fmtUsd(data.stat?.bonus_usd_allowance)}) — alap havi {fmtMex(data.stat?.base_monthly_mxc)} MXC / {fmtUsd(data.stat?.base_monthly_usd)} → effektív {fmtMex(data.stat?.effective_monthly_mxc)} MXC / {fmtUsd(data.stat?.effective_monthly_usd)}
                </div>
              )}
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-3">Mai fogyás és aktivitás</div>
              <div className="grid grid-cols-4 gap-3">
                <RawVfMxUsdStat
                  label="Mai fogyás"
                  rawMxc={data.stat?.today_mex_credit}
                  rawUsd={data.stat?.today_cost}
                  vfMxc={data.stat?.today_vf_mex_credit}
                  vfUsd={data.stat?.today_vf_cost}
                  testId="drilldown-today-raw-vf"
                />
                <MiniStat label="Utolsó belépés"   value={fmtTs(data.user?.last_login_at)} small />
                <MiniStat label="Aktivitás · mai / havi gen." value={`${data.stat?.today_generations ?? 0} / ${data.stat?.month_generations ?? 0}`} small />
                <MiniStat label="Role · Státusz"   value={`${data.user?.role || "—"} · ${data.user?.status || "—"}`} small />
              </div>
              {data.stat?.guard_would_block_mex && (
                <div className="mt-3 rounded-lg px-3 py-2 text-[12px]" style={{ background: "rgba(251,113,133,0.08)", border: "1px solid rgba(251,113,133,0.28)", color: "#fca5b8" }}>
                  ⚠ Ez a tester MXC szempontból KIFOGYOTT. Módszer: „<b>{ {monitor_only:"Csak mérés",admin_test:"Belső teszt",enforced:"Éles MXC védelem"}[data.stat.guard_mode] || data.stat.guard_mode }</b>” — {data.stat.guard_mode === "monitor_only" ? "csak mérünk, nem blokkolunk. A napi/havi USD budget külön blokkolja." : data.stat.guard_mode === "admin_test" ? "csak belső teszt useren blokkolunk, éles tester nem érintett." : "kifogyás esetén a rendszer megállítja a generálást."}
                </div>
              )}
            </section>

            {/* BREAKDOWN */}
            <BreakdownTable rows={data.breakdown || []} />

            {/* RECENT EVENTS */}
            <RecentEvents events={data.recent_events || []} />
          </div>
        )}
      </div>
      {showLimitsEdit && (
        <LimitsEditModal
          email={email}
          override={data?.stat?.override_details || {}}
          stat={data?.stat || {}}
          onClose={() => setShowLimitsEdit(false)}
          onSaved={() => { setShowLimitsEdit(false); load(); onDataChange?.(); }}
        />
      )}
      {showBonusEdit && (
        <BonusMxcEditModal
          email={email}
          stat={data?.stat || {}}
          onClose={() => setShowBonusEdit(false)}
          onSaved={() => { setShowBonusEdit(false); load(); onDataChange?.(); }}
        />
      )}
    </div>
  );
}

function BonusMxcEditModal({ email, stat, onClose, onSaved }) {
  const mexPerUsd = stat.mex_per_usd || 50;
  const baseMxc = stat.base_monthly_mxc || 0;
  const currentExtraMxc = stat.extra_mex_credit || 0;
  const [value, setValue] = useState(String(currentExtraMxc));
  const [saving, setSaving] = useState(false);
  const previewExtra = !isNaN(parseFloat(value)) && parseFloat(value) >= 0 ? parseFloat(value) : currentExtraMxc;
  const previewEffective = baseMxc + previewExtra;

  const save = async (customVal) => {
    setSaving(true);
    try {
      const next = customVal != null ? customVal : parseFloat(value);
      if (isNaN(next) || next < 0) {
        toast.error("Érvényes MXC összeg szükséges.");
        setSaving(false);
        return;
      }
      await api.patch(`/admin/users/${encodeURIComponent(email)}/bonus-mxc`, {
        extra_mex_credit: Number(next.toFixed(4)),
        note: next === 0 ? "Adminból törölt bonus allowance" : `Adminból beállított bonus allowance: ${next} MXC`,
      });
      toast.success(next === 0 ? "Bónusz MXC törölve." : `Bónusz MXC beállítva: ${fmtMex(next)} MXC`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bónusz MXC mentése sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center px-4" style={{ background: "rgba(8,8,12,0.86)", backdropFilter: "blur(8px)" }} onClick={onClose} data-testid="bonus-mxc-edit-overlay">
      <div className="relative w-full max-w-[520px] rounded-2xl p-6 text-white" style={{ background: "#16161f", border: "1px solid rgba(255,255,255,0.08)" }} onClick={(e) => e.stopPropagation()} data-testid="bonus-mxc-edit-modal">
        <button onClick={onClose} data-testid="bonus-mxc-edit-close" className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/[0.06]"><X size={14} /></button>
        <h3 className="font-heading text-[17px] font-semibold mb-1">Extra / bónusz MXC</h3>
        <p className="text-[12px] text-[#a1a1a1] mb-5">{email} — ez külön mező, nem a havi usage és nem a havi limit override.</p>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <MxUsdStat label="Alap havi limit" mxc={baseMxc} usd={stat.base_monthly_usd} accent="#ffdb5c" testId="bonus-edit-base-monthly" />
          <MxUsdStat label="Extra MXC" mxc={previewExtra} usd={previewExtra / mexPerUsd} accent={previewExtra > 0 ? "#a294ff" : "#7c7c8a"} testId="bonus-edit-extra-mxc" />
          <MxUsdStat label="Effektív havi keret" mxc={previewEffective} usd={previewEffective / mexPerUsd} accent="#4ade80" testId="bonus-edit-effective-mxc" />
        </div>
        <div className="rounded-lg px-3 py-2 text-[11px] mb-4" style={{ background: "rgba(117,223,245,0.08)", border: "1px solid rgba(117,223,245,0.16)", color: "#b9eff8" }} data-testid="bonus-mxc-help-note">
          A <b>bónusz MXC</b> ugyanaz a user-specifikus allowance, ami eddig az extra creditből jött. Itt pontos értékre állíthatod, vagy <b>0 MXC-re</b> teheted, ami ténylegesen eltávolítja.
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {[0, 50, 100, 300].map((preset) => (
            <button
              key={preset}
              onClick={() => save(preset)}
              disabled={saving}
              data-testid={`bonus-mxc-preset-${preset}`}
              className="px-3 py-2 rounded-lg text-[13px] font-semibold bg-white/[0.05] hover:bg-white/[0.12] text-[#c8c8d2] disabled:opacity-40"
            >
              <div className="text-[#ffdb5c]">{fmtMex(preset)} MXC</div>
              <div className="text-[10px] text-[#7c7c8a] mt-0.5">{fmtUsd(preset / mexPerUsd)}</div>
            </button>
          ))}
        </div>
        <label className="block mb-4">
          <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Extra / bónusz MXC</span>
          <div className="flex items-center gap-2">
            <input type="number" step="10" min="0" value={value} onChange={(e) => setValue(e.target.value)} data-testid="bonus-mxc-custom-input"
              className="w-full pl-4 pr-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]" />
            <button onClick={() => save(null)} disabled={saving} data-testid="bonus-mxc-save-custom"
              className="px-4 py-2.5 rounded-lg text-[13px] font-semibold text-white"
              style={{ background: "linear-gradient(135deg, #5e50f7, #4F46E5)", opacity: saving ? 0.6 : 1 }}>
              {saving ? "…" : "Mentés"}
            </button>
          </div>
          <div className="text-[10.5px] text-[#7c7c8a] mt-1.5">0 = allowance törlése · ≈ {fmtUsd(previewExtra / mexPerUsd)}</div>
        </label>
        {currentExtraMxc > 0 && (
          <button onClick={() => save(0)} disabled={saving} data-testid="bonus-mxc-clear"
            className="w-full px-3 py-2 rounded-lg text-[12px] font-semibold text-[#fb7185] hover:bg-[#fb718522]">
            Bónusz MXC teljes eltávolítása
          </button>
        )}
      </div>
    </div>
  );
}

function LimitsEditModal({ email, override, stat, onClose, onSaved }) {
  const mexPerUsd = stat.mex_per_usd || 50;
  const [initialCredit, setInitialCredit] = useState(override.initial_credit ?? "");
  // Költségkeret-alapú limitek — kétirányú MXC↔USD szinkron (1 USD = mexPerUsd MXC).
  const [dailyMxc, setDailyMxc] = useState(override.daily_cost_usd != null ? String((override.daily_cost_usd * mexPerUsd).toFixed(1)) : "");
  const [dailyUsd, setDailyUsd] = useState(override.daily_cost_usd != null ? String(Number(override.daily_cost_usd).toFixed(2)) : "");
  const [monthlyMxc, setMonthlyMxc] = useState(override.monthly_cost_usd != null ? String((override.monthly_cost_usd * mexPerUsd).toFixed(1)) : "");
  const [monthlyUsd, setMonthlyUsd] = useState(override.monthly_cost_usd != null ? String(Number(override.monthly_cost_usd).toFixed(2)) : "");
  const [creditPlan, setCreditPlan] = useState(override.credit_plan ?? stat.credit_plan ?? "");
  const [saving, setSaving] = useState(false);
  const planOptions = ["", "MEX", "MEX+", "SPRINT", "EGYEDI"];

  const onDailyMxc = (v) => { setDailyMxc(v); const n = parseFloat(v); setDailyUsd(v === "" || isNaN(n) ? "" : (n / mexPerUsd).toFixed(2)); };
  const onDailyUsd = (v) => { setDailyUsd(v); const n = parseFloat(v); setDailyMxc(v === "" || isNaN(n) ? "" : (n * mexPerUsd).toFixed(1)); };
  const onMonthlyMxc = (v) => { setMonthlyMxc(v); const n = parseFloat(v); setMonthlyUsd(v === "" || isNaN(n) ? "" : (n / mexPerUsd).toFixed(2)); };
  const onMonthlyUsd = (v) => { setMonthlyUsd(v); const n = parseFloat(v); setMonthlyMxc(v === "" || isNaN(n) ? "" : (n * mexPerUsd).toFixed(1)); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      const put = (k, v) => { if (v !== "" && !isNaN(parseFloat(v))) payload[k] = parseFloat(v); };
      put("initial_credit", initialCredit);
      if (dailyUsd !== "" && !isNaN(parseFloat(dailyUsd))) payload.daily_cost_usd = Number(parseFloat(dailyUsd).toFixed(4));
      if (monthlyUsd !== "" && !isNaN(parseFloat(monthlyUsd))) payload.monthly_cost_usd = Number(parseFloat(monthlyUsd).toFixed(4));
      payload.credit_plan = creditPlan || "NONE";
      await api.put(`/admin/users/${encodeURIComponent(email)}/limits`, payload);
      toast.success("Limitek elmentve.");
      onSaved?.();
    } catch { toast.error("Mentés sikertelen."); }
    finally { setSaving(false); }
  };
  const clearOverride = async () => {
    if (!window.confirm("Biztosan törlöd az egyedi override-ot? A user visszatér a globális limitekre.")) return;
    setSaving(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(email)}/limits`, { clear_override: true });
      toast.success("Override törölve.");
      onSaved?.();
    } catch { toast.error("Törlés sikertelen."); }
    finally { setSaving(false); }
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const F = (label, val, setVal, isMoney = false) => (
    <label className="block">
      <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">{label}</span>
      <div className="relative">
        {isMoney && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7c7c8a]">$</span>}
        <input type="number" step={isMoney ? "0.01" : "1"} min="0" value={val} onChange={(e) => setVal(e.target.value)}
          className={`w-full ${isMoney ? "pl-7" : "pl-4"} pr-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]`}
          placeholder="üresen hagyva → globális" />
      </div>
    </label>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4" style={{ background: "rgba(8,8,12,0.86)", backdropFilter: "blur(8px)" }} onClick={onClose} data-testid="limits-edit-overlay">
      <div className="relative w-full max-w-[520px] rounded-2xl p-6 text-white" style={{ background: "#16161f", border: "1px solid rgba(255,255,255,0.08)" }} onClick={(e) => e.stopPropagation()} data-testid="limits-edit-modal">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/[0.06]"><X size={14} /></button>
        <h3 className="font-heading text-[17px] font-semibold mb-1">Egyedi limit override</h3>
        <p className="text-[12px] text-[#a1a1a1] mb-5">{email} — üresen hagyott mezők a globális pilot limitet használják.</p>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <MxUsdStat label="Alap havi limit" mxc={stat.base_monthly_mxc} usd={stat.base_monthly_usd} accent="#ffdb5c" testId="limits-base-monthly" />
          <MxUsdStat label="Extra MXC" mxc={stat.extra_mex_credit} usd={stat.bonus_usd_allowance} accent={(stat.extra_mex_credit ?? 0) > 0 ? "#a294ff" : "#7c7c8a"} testId="limits-extra-mxc" />
          <MxUsdStat label="Effektív havi keret" mxc={stat.effective_monthly_mxc} usd={stat.effective_monthly_usd} accent="#4ade80" testId="limits-effective-monthly" />
        </div>
        <div className="rounded-lg px-3 py-2 text-[11px] mb-5" style={{ background: "rgba(117,223,245,0.08)", border: "1px solid rgba(117,223,245,0.16)", color: "#b9eff8" }} data-testid="limits-bonus-separation-note">
          A <b>Havi MXC keret</b> és az <b>Extra / bónusz MXC</b> külön adat. A bonus allowance szerkesztéséhez használd a user részletező felső sávjában a <b>Bónusz MXC…</b> gombot.
        </div>
        <div className="space-y-4">
          <label className="block">
            <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">MXC csomag alap</span>
            <select
              value={creditPlan}
              onChange={(e) => setCreditPlan(e.target.value)}
              data-testid="limits-edit-credit-plan"
              className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]"
            >
              {planOptions.map((plan) => <option key={plan || "none"} value={plan}>{plan || "Globális alap"}</option>)}
            </select>
          </label>
          {F("Induló MEX kredit", initialCredit, setInitialCredit)}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Napi MXC limit</span>
              <input type="number" step="10" min="0" value={dailyMxc} onChange={(e) => onDailyMxc(e.target.value)}
                data-testid="limits-edit-daily-mxc"
                className="w-full pl-4 pr-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]"
                placeholder="üresen hagyva → globális" />
            </label>
            <label className="block">
              <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Napi $ limit</span>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7c7c8a]">$</span>
                <input type="number" step="0.01" min="0" value={dailyUsd} onChange={(e) => onDailyUsd(e.target.value)}
                  data-testid="limits-edit-daily-usd"
                  className="w-full pl-7 pr-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]"
                  placeholder="üresen hagyva → globális" />
              </div>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Havi MXC keret</span>
              <input type="number" step="10" min="0" value={monthlyMxc} onChange={(e) => onMonthlyMxc(e.target.value)}
                data-testid="limits-edit-monthly-mxc"
                className="w-full pl-4 pr-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]"
                placeholder="üresen hagyva → csomag default" />
            </label>
            <label className="block">
              <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Havi $ keret</span>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7c7c8a]">$</span>
                <input type="number" step="0.01" min="0" value={monthlyUsd} onChange={(e) => onMonthlyUsd(e.target.value)}
                  data-testid="limits-edit-monthly-usd"
                  className="w-full pl-7 pr-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]"
                  placeholder="üresen hagyva → csomag default" />
              </div>
            </label>
          </div>
          <div className="text-[10.5px] text-[#7c7c8a]">1 USD = {mexPerUsd} MXC · a mezők kétirányban szinkronizálódnak · csak ennek a usernek override</div>
        </div>
        <div className="flex items-center gap-2 mt-6">
          <button onClick={save} disabled={saving} data-testid="limits-edit-save"
            className="px-5 py-2.5 rounded-xl font-semibold text-white flex-1"
            style={{ background: "linear-gradient(135deg, #5e50f7, #4F46E5)", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Mentés…" : "Mentés"}
          </button>
          <button onClick={clearOverride} disabled={saving} data-testid="limits-edit-clear"
            className="px-3 py-2.5 rounded-xl text-[12.5px] font-semibold text-[#fb7185] hover:bg-[#fb718522]">
            Limit override törlése
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent = "#e4e4ec", small = false, dataTestId, note = null }) {
  return (
    <div className="rounded-xl px-4 py-3 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.6)" }} data-testid={dataTestId}>
      <div className="text-[10px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-1">{label}</div>
      <div className={small ? "text-[13px] font-medium" : "text-[16px] font-semibold"} style={{ color: accent }}>{value ?? "—"}</div>
      {note && <div className="text-[10px] text-white/45 mt-1">{note}</div>}
    </div>
  );
}

/* Sprint 3.22 v2 — MXC primary + USD secondary stat card. */
function MxUsdStat({ label, mxc, usd, adminUnlimited = false, accent = "#ffdb5c", testId }) {
  if (adminUnlimited) {
    return (
      <div className="rounded-xl px-4 py-3 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.6)" }} data-testid={testId}>
        <div className="text-[10px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-1">{label}</div>
        <div className="text-[15px] font-semibold" style={{ color: "#a294ff" }}>Korlátlan</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl px-4 py-3 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.6)" }} data-testid={testId}>
      <div className="text-[10px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-1">{label}</div>
      <div className="text-[20px] font-bold leading-tight" style={{ color: accent }}>
        {fmtMex(mxc)} <span className="text-[11px] font-medium opacity-60">MXC</span>
      </div>
      <div className="text-[13px] mt-1.5" style={{ color: "#75dff5" }}>{fmtUsd(usd)}</div>
    </div>
  );
}

function BreakdownTable({ rows }) {
  const { sorted, key, dir, toggle } = useSort(rows, "count", "desc");
  if (rows.length === 0) return (
    <section><div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-3">Agentenkénti / kártyánkénti bontás</div>
      <div className="text-[13px] text-[#a1a1a1] py-6 text-center">Ennél a usernél még nincs mért használat.</div>
    </section>
  );
  const grid = "grid-cols-[100px_130px_120px_120px_70px_80px_190px_190px_130px]";
  return (
    <section>
      <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-3">Agentenkénti / kártyánkénti bontás</div>
      <div className="rounded-xl border border-white/[0.06] overflow-hidden" style={{ background: "rgba(20,20,26,0.5)" }}>
        <div className="overflow-x-auto">
          <div className="min-w-[1320px]">
            <div className={`grid ${grid} gap-2 px-4 py-2.5 border-b border-white/[0.06] items-center`}>
              <SortHeader label="Réteg" active={key==="layer"} dir={dir} onClick={() => toggle("layer")} />
              <SortHeader label="Ügynök" active={key==="agent_name"} dir={dir} onClick={() => toggle("agent_name")} />
              <SortHeader label="Poszttípus" active={key==="sub_agent"} dir={dir} onClick={() => toggle("sub_agent")} />
              <SortHeader label="Művelet" active={key==="request_type"} dir={dir} onClick={() => toggle("request_type")} />
              <SortHeader label="Db" active={key==="count"} dir={dir} onClick={() => toggle("count")} align="right" />
              <SortHeader label="ECU (tech.)" active={key==="ecu"} dir={dir} onClick={() => toggle("ecu")} align="right" />
              <SortHeader label="Összköltség" active={key==="vf_usd_cost"} dir={dir} onClick={() => toggle("vf_usd_cost")} align="right" />
              <SortHeader label="Átlag / hívás" active={key==="avg_vf_usd"} dir={dir} onClick={() => toggle("avg_vf_usd")} align="right" />
              <SortHeader label="Utolsó" active={key==="last_used"} dir={dir} onClick={() => toggle("last_used")} />
            </div>
            {sorted.map((r, i) => (
              <div key={i} className={`grid ${grid} gap-2 px-4 py-2 border-b border-white/[0.04] last:border-b-0 text-[12px] items-center`}>
                <div className="text-[#a1a1a1]">{labelLayer(r.layer)}</div>
                <div className="text-[#c8c8d2] font-medium truncate">{labelAgent(r.agent_name)}</div>
                <div className="text-[#a294ff] truncate">{labelSub(r.sub_agent)}</div>
                <div className="text-[#75dff5] truncate">{labelReq(r.request_type)}</div>
                <div className="text-right">{r.count}</div>
                <div className="text-right text-[#75dff5]">{fmtEcu(r.ecu)}</div>
                <RawVfCostCell rawMxc={r.mex_credit} rawUsd={r.usd_cost} vfMxc={r.vf_mex_credit} vfUsd={r.vf_usd_cost} testId={`breakdown-cost-${i}`} />
                <RawVfCostCell rawMxc={r.avg_mex_credit} rawUsd={r.avg_usd} vfMxc={r.avg_vf_mex_credit} vfUsd={r.avg_vf_usd} testId={`breakdown-avg-${i}`} />
                <div className="text-[11px] text-[#a1a1a1]">{fmtTs(r.last_used)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function RecentEvents({ events }) {
  if (events.length === 0) return null;
  const grid = "grid-cols-[130px_130px_120px_120px_70px_80px_200px_90px_50px_50px]";
  return (
    <section>
      <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-3">Legutóbbi usage események (max 50)</div>
      <div className="rounded-xl border border-white/[0.06] overflow-hidden" style={{ background: "rgba(20,20,26,0.5)" }}>
        <div className="overflow-x-auto">
          <div className="min-w-[1250px]">
            <div className={`grid ${grid} gap-2 px-4 py-2.5 text-[10.5px] tracking-[0.1em] uppercase text-[#7c7c8a] border-b border-white/[0.06]`}>
              <div>Időpont</div><div>Ügynök</div><div>Poszttípus</div><div>Művelet</div>
              <div>Státusz</div>
              <div className="text-right">ECU</div>
              <div className="text-right">Korrigált</div>
              <div className="truncate">Model</div>
              <div className="text-right">In</div>
              <div className="text-right">Out</div>
            </div>
            {events.map((e) => (
              <div key={e.id || e.created_at} className={`grid ${grid} gap-2 px-4 py-2 text-[11.5px] items-center border-b border-white/[0.04] last:border-b-0`}>
                <div className="text-[#a1a1a1]">{fmtTs(e.created_at)}</div>
                <div className="text-[#c8c8d2] truncate">{labelAgent(e.agent_name)}</div>
                <div className="text-[#a294ff] truncate">{labelSub(e.sub_agent)}</div>
                <div className="text-[#75dff5] truncate">{labelReq(e.request_type)}</div>
                <div><LogStatus s={e.status} /></div>
                <div className="text-right text-[#75dff5]">{fmtEcu(e.estimated_ecu)}</div>
                <RawVfCostCell rawMxc={e.raw_estimated_mxc} rawUsd={e.raw_estimated_usd} vfMxc={e.vf_mxc} vfUsd={e.vf_usd} testId={`recent-event-cost-${e.id || e.created_at}`} />
                <div className="text-[#a1a1a1] truncate">{e.model_name || e.model || "—"}</div>
                <div className="text-right text-[#a1a1a1]">{e.input_tokens ?? e.estimated_input_tokens ?? "—"}</div>
                <div className="text-right text-[#a1a1a1]">{e.output_tokens ?? e.estimated_output_tokens ?? "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── AI usage log tab ─────────── */
export function UsageLogTab({ mode = "calibrated" }) {
  const [logs, setLogs] = useState([]);
  const [overview, setOverview] = useState(null);
  const [filters, setFilters] = useState({ user_email: "", agent_name: "", status: "" });
  const [busy, setBusy] = useState(true);
  const normalizedMode = normalizeAdminUsageMode(mode);
  const load = useCallback(async () => {
    setBusy(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    try {
      const [l, o] = await Promise.all([
        api.get("/admin/usage/logs", { params }),
        api.get("/admin/usage/overview"),
      ]);
      setLogs(l.data.logs || []);
      setOverview(o.data || null);
    } finally { setBusy(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load, normalizedMode]);
  useAdminUsageRefresh(load);

  const { sorted, key, dir, toggle } = useSort(logs, "created_at", "desc");
  const gridTemplate = "90px minmax(115px,0.72fr) minmax(125px,0.88fr) 86px 92px 82px 74px 118px 118px";

  return (
    <div data-testid="usage-log-table">
      {/* Summary strip */}
      {overview && (
        <div className="grid grid-cols-4 gap-3 mb-4">
          <MiniStat label="Mai gen." value={overview.today_generations} />
          <MiniStat label="Havi gen." value={overview.month_generations} />
          <MiniStat label="Mai ECU" value={fmtEcu(normalizedMode === "estimated" ? overview.today_estimated_ecu : overview.today_ecu)} accent="#a294ff" dataTestId="usage-log-summary-today-actual-ecu" note={overview.today_is_estimated ? "Becsült" : null} />
          <MiniStat label="Havi ECU" value={fmtEcu(normalizedMode === "estimated" ? overview.month_estimated_ecu : overview.month_ecu)} accent="#a294ff" dataTestId="usage-log-summary-month-actual-ecu" note={overview.month_has_uncalibrated_days ? "Részben becsült" : null} />
          <MiniStat label="Mai USD" value={fmtUsd(normalizedMode === "estimated" ? overview.today_cost : overview.today_vf_cost)} accent="#75dff5" small />
          <MiniStat label="Havi USD" value={fmtUsd(normalizedMode === "estimated" ? overview.month_cost : overview.month_vf_cost)} accent="#75dff5" small />
          <MiniStat label="Mai MXC" value={`${fmtMex(normalizedMode === "estimated" ? overview.today_mex_credit : overview.today_vf_mex_credit)} MXC`} accent="#ffdb5c" small />
          <MiniStat label="Havi MXC" value={`${fmtMex(normalizedMode === "estimated" ? overview.month_mex_credit : overview.month_vf_mex_credit)} MXC`} accent="#ffdb5c" small />
        </div>
      )}
      <div className="flex gap-2 mb-4 text-[13px]">
        <input value={filters.user_email} onChange={(e) => setFilters((f) => ({ ...f, user_email: e.target.value }))} placeholder="user e-mail…" className="flex-1 px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] focus:outline-none focus:border-[#5e50f7]" data-testid="usage-log-filter-user" />
        <input value={filters.agent_name} onChange={(e) => setFilters((f) => ({ ...f, agent_name: e.target.value }))} placeholder="ügynök kulcs…" className="w-[180px] px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] focus:outline-none focus:border-[#5e50f7]" data-testid="usage-log-filter-agent" />
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="w-[150px] px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] focus:outline-none focus:border-[#5e50f7]" data-testid="usage-log-filter-status">
          <option value="">Összes státusz</option><option value="success">Sikeres</option><option value="error">Hiba</option><option value="blocked_by_limit">Limitre blokkolt</option>
        </select>
        <button onClick={load} className="px-3 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08]" title="Frissítés" data-testid="usage-log-refresh"><ArrowsCounterClockwise size={14} /></button>
      </div>
      <div className="text-[11px] text-[#8a8a99] mb-3" data-testid="usage-log-hierarchy-note">
        Aktív nézet: <b>{normalizedMode === "estimated" ? "raw becslés" : "valós ECU-korrigált réteg"}</b>. A faktor oszlop csak audit információ.
      </div>
      <div className="rounded-2xl overflow-hidden border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.6)" }}>
        <div className="overflow-x-auto">
          <div className="min-w-[980px]">
            <div className="grid gap-1.5 px-2.5 py-1.5 border-b border-white/[0.06] items-center" style={{ gridTemplateColumns: gridTemplate }}>
              <SortHeader label="Időpont" active={key==="created_at"} dir={dir} onClick={() => toggle("created_at")} />
              <SortHeader label="User" active={key==="user_email"} dir={dir} onClick={() => toggle("user_email")} />
              <SortHeader label="Workflow" active={key==="agent_name"} dir={dir} onClick={() => toggle("agent_name")} />
              <SortHeader label="ECU" active={key===(normalizedMode === "estimated" ? "estimated_ecu" : "calibrated_ecu")} dir={dir} onClick={() => toggle(normalizedMode === "estimated" ? "estimated_ecu" : "calibrated_ecu")} align="right" />
              <SortHeader label="Faktor" active={key==="correction_factor"} dir={dir} onClick={() => toggle("correction_factor")} align="right" />
              <SortHeader label="USD" active={key===(normalizedMode === "estimated" ? "estimated_usd" : "effective_usd")} dir={dir} onClick={() => toggle(normalizedMode === "estimated" ? "estimated_usd" : "effective_usd")} align="right" />
              <SortHeader label="MXC" active={key===(normalizedMode === "estimated" ? "estimated_mxc" : "effective_mxc")} dir={dir} onClick={() => toggle(normalizedMode === "estimated" ? "estimated_mxc" : "effective_mxc")} align="right" />
              <SortHeader label="Áll." active={key==="status"} dir={dir} onClick={() => toggle("status")} />
              <SortHeader label="Technikai" active={key==="model_name"} dir={dir} onClick={() => toggle("model_name")} align="right" />
            </div>
            {busy ? <div className="px-5 py-10 text-center text-[#a1a1a1] text-sm">Betöltés…</div> :
             sorted.length === 0 ? <div className="px-5 py-10 text-center text-[#a1a1a1] text-sm">Nincs találat.</div> :
             sorted.map((l) => (
               <div key={l.id} className="grid gap-1.5 px-2.5 py-1 items-center text-[10.5px] border-b border-white/[0.04] last:border-b-0" style={{ gridTemplateColumns: gridTemplate }} data-testid={`usage-log-row-${l.id}`}>
                 <div className="text-[#a1a1a1]">{fmtTs(l.created_at)}</div>
                 <div className="truncate text-[#c8c8d2]" title={l.user_email}>{l.user_email || "—"}</div>
                 <UsageLogWorkflowCell log={l} />
                  <UsageLogEstimatedEcuCell log={l} mode={normalizedMode} />
                  <UsageLogCalibratedEcuCell log={l} mode={normalizedMode} />
                  <UsageLogUsdCell log={l} mode={normalizedMode} />
                  <UsageLogMxcCell log={l} mode={normalizedMode} />
                 <div><LogStatus s={l.status} /></div>
                  <UsageLogTechnicalCell log={l} />
               </div>
             ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LogStatus({ s }) {
  const map = { success: ["#4ade80", "OK"], error: ["#fb7185", "Hiba"], blocked_by_limit: ["#fbbf24", "Limit"] };
  const [c, t] = map[s] || ["#a1a1a1", s];
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${c}22`, color: c }}>{t}</span>;
}

/* ─────────── Pilot settings tab ─────────── */
export function PilotSettingsTab() {
  const [s, setS] = useState(null);
  const [creditCfg, setCreditCfg] = useState(null);
  const [ecuItems, setEcuItems] = useState([]);
  const [yesterdayCalibration, setYesterdayCalibration] = useState(null);
  const [selectedCalibration, setSelectedCalibration] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [ecuForm, setEcuForm] = useState({
    date: "",
    actual_app_ecu: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const [savingCredit, setSavingCredit] = useState(false);
  const [savingEcu, setSavingEcu] = useState(false);
  const [deletingEcuDate, setDeletingEcuDate] = useState(null);
  const [planCounts, setPlanCounts] = useState({});
  const datePickerRef = useRef(null);
  const defaultCalibrationDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  const loadEcuCorrections = useCallback(async (selectedDate = defaultCalibrationDate) => {
    const response = await api.get(`/admin/ecu-corrections?limit=21&date=${encodeURIComponent(selectedDate)}`);
    setEcuItems(response.data.items || []);
    setYesterdayCalibration(response.data.yesterday || null);
    const dayData = response.data.selected_day || response.data.yesterday || null;
    setSelectedCalibration(dayData);
    setEcuForm((prev) => ({
      ...prev,
      date: dayData?.date || selectedDate,
      actual_app_ecu: dayData?.actual_app_ecu != null ? String(dayData.actual_app_ecu) : "",
      note: dayData?.note || "",
    }));
  }, [defaultCalibrationDate]);

  useEffect(() => {
    (async () => {
      const [a, b, c] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/credit-config"),
        api.get(`/admin/ecu-corrections?limit=21&date=${encodeURIComponent(defaultCalibrationDate)}`),
      ]);
      setS(a.data);
      try { const pc = await api.get("/admin/credit-plans/usage-counts"); setPlanCounts(pc.data.counts || {}); } catch { /* non-fatal */ }
      setCreditCfg(b.data);
      setEcuItems(c.data.items || []);
      setYesterdayCalibration(c.data.yesterday || null);
      setSelectedCalibration(c.data.selected_day || c.data.yesterday || null);
      setEcuForm((prev) => ({
        ...prev,
        date: c.data.selected_day?.date || c.data.yesterday?.date || defaultCalibrationDate,
        actual_app_ecu: c.data.selected_day?.actual_app_ecu != null ? String(c.data.selected_day.actual_app_ecu) : "",
        note: c.data.selected_day?.note || "",
      }));
    })();
  }, [defaultCalibrationDate]);

  if (!s || !creditCfg) return <div className="px-6 py-12 text-center text-[#a1a1a1] text-sm">Betöltés…</div>;

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Mentve."); }
    catch { toast.error("Mentés sikertelen."); }
    finally { setSaving(false); }
  };
  const saveCredit = async () => {
    const affectedPlans = ["MEX", "MEX+", "SPRINT", "EGYEDI"].filter((k) => (planCounts[k] || 0) > 0);
    const totalAffected = affectedPlans.reduce((s2, k) => s2 + (planCounts[k] || 0), 0);
    const detail = affectedPlans.map((k) => `${k}: ${planCounts[k]} felhasználó`).join("\n");
    const msg = totalAffected > 0
      ? `A csomag-alapkeretek módosítása ${totalAffected} felhasználót érint (akiknek NINCS egyedi override-juk):\n\n${detail}\n\nAz egyedi override-os userek NEM változnak. Folytatod?`
      : "Csomag-alapkeretek mentése. Folytatod?";
    if (!window.confirm(msg)) return;
    setSavingCredit(true);
    try {
      const r = await api.put("/admin/credit-config", {
        guard_mode: creditCfg.guard_mode,
        default_initial_credit: creditCfg.default_initial_credit,
        mex_per_usd: creditCfg.mex_per_usd,
        cost_estimation_multiplier: creditCfg.cost_estimation_multiplier,
        credit_plans: creditCfg.credit_plans,
      });
      setCreditCfg(r.data);
      toast.success("MXC beállítások mentve.");
    } catch { toast.error("MXC beállítások mentése sikertelen."); }
    finally { setSavingCredit(false); }
  };
  const setPlanInitialCredit = (planKey, rawValue) => {
    const initial_credit = parseFloat(rawValue) || 0;
    setCreditCfg((current) => ({
      ...current,
      credit_plans: {
        ...(current.credit_plans || {}),
        [planKey]: {
          ...((current.credit_plans || {})[planKey] || {}),
          initial_credit,
          monthly_cost_usd: Number((initial_credit / 50).toFixed(2)),
        },
      },
    }));
  };
  const setPlanMonthlyUsd = (planKey, rawValue) => {
    const monthly_cost_usd = parseFloat(rawValue) || 0;
    setCreditCfg((current) => ({
      ...current,
      credit_plans: {
        ...(current.credit_plans || {}),
        [planKey]: {
          ...((current.credit_plans || {})[planKey] || {}),
          monthly_cost_usd,
          initial_credit: Number((monthly_cost_usd * 50).toFixed(1)),
        },
      },
    }));
  };
  const setPlanDailyUsd = (planKey, rawValue) => {
    const daily_cost_usd = parseFloat(rawValue) || 0;
    setCreditCfg((current) => ({
      ...current,
      credit_plans: {
        ...(current.credit_plans || {}),
        [planKey]: { ...((current.credit_plans || {})[planKey] || {}), daily_cost_usd },
      },
    }));
  };
  const setPlanDailyMxc = (planKey, rawValue) => {
    const mpu = creditCfg.mex_per_usd || 50;
    const daily_cost_usd = Number(((parseFloat(rawValue) || 0) / mpu).toFixed(4));
    setCreditCfg((current) => ({
      ...current,
      credit_plans: {
        ...(current.credit_plans || {}),
        [planKey]: { ...((current.credit_plans || {})[planKey] || {}), daily_cost_usd },
      },
    }));
  };
  const saveEcu = async () => {
    setSavingEcu(true);
    try {
      const payload = {
        date: ecuForm.date,
        actual_app_ecu: parseFloat(ecuForm.actual_app_ecu || 0),
        actual_development_ecu: null,
        note: ecuForm.note || "",
      };
      if (!payload.date) {
        toast.error("Adj meg dátumot.");
        setSavingEcu(false);
        return;
      }
      if (Number.isNaN(payload.actual_app_ecu) || payload.actual_app_ecu < 0) {
        toast.error("Adj meg érvényes app ECU értéket.");
        setSavingEcu(false);
        return;
      }
      await api.post("/admin/ecu-corrections", payload);
      await loadEcuCorrections(payload.date || defaultCalibrationDate);
      emitAdminUsageRefresh();
      toast.success("ECU kalibráció mentve.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ECU korrekció mentése sikertelen.");
    } finally { setSavingEcu(false); }
  };
  const onCalibrationDateChange = async (newDate) => {
    if (!newDate) return;
    try {
      await loadEcuCorrections(newDate);
      setShowDatePicker(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A kiválasztott nap betöltése sikertelen.");
    }
  };
  const deleteEcu = async (dayKey) => {
    setDeletingEcuDate(dayKey);
    try {
      await api.delete(`/admin/ecu-corrections/${encodeURIComponent(dayKey)}`);
      await loadEcuCorrections(ecuForm.date || defaultCalibrationDate);
      emitAdminUsageRefresh();
      toast.success("ECU rekord törölve.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ECU rekord törlése sikertelen.");
    } finally {
      setDeletingEcuDate(null);
    }
  };
  const field = (k, label, isCost) => (
    <label className="block">
      <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">{label}</span>
      <div className="relative">
        {isCost && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7c7c8a]">$</span>}
        <input
          type="number"
          step={isCost ? "0.01" : "1"}
          value={s[k]}
          onChange={(e) => setS((v) => ({ ...v, [k]: isCost ? parseFloat(e.target.value) : parseInt(e.target.value, 10) || 0 }))}
          className={`w-full ${isCost ? "pl-7" : "pl-4"} pr-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]`}
        />
      </div>
    </label>
  );
  // Sprint 3.6+: UI-ban csak 2 mód látszik. Az `admin_test` régi rekordokat
  // kezelve marad, de a felhasználó váltásához nem elérhető (fejlesztői
  // config-értékként megmarad, de a segmented control-ban nem szerepel).
  const guardOptions = [
    {
      key: "monitor_only",
      color: "#8a7cff",
      label: "Csak mérés",
      desc: "MXC-t számolunk és jelezzük a kerethasználatot, de MXC alapján nem blokkolunk. A napi / havi generálási és USD limitek külön működnek.",
    },
    {
      key: "enforced",
      color: "#c4b5fd",
      label: "Éles MXC védelem",
      desc: "Ha egy normál user elfogyasztja az MXC keretét, a rendszer megállítja az új generálást. Admint sose blokkolja.",
    },
  ];
  const isLegacyMode = !guardOptions.find((o) => o.key === creditCfg.guard_mode);
  const calibrationDay = selectedCalibration || yesterdayCalibration;
  const calibrationSourceMeta = getFactorSourceMeta(calibrationDay?.factor_source);
  return (
    <div data-testid="pilot-settings-tab" className="space-y-10">
      {/* ═══════ 1. USD BUDGET LIMIT (FŐ KONTROLL) ═══════ */}
      <div>
        <h3 className="font-heading text-[18px] font-semibold mb-1">Tester USD budget (fő kontroll)</h3>
        <p className="text-[12.5px] text-[#a1a1a1] mb-2">
          Ez az egyetlen limit, ami tényleg blokkolja a normál usereket.
          <b> A generálásszám nem blokkol</b> — csak aktivitás mérés.
        </p>
        <p className="text-[11.5px] text-[#7c7c8a] mb-5">Adminokra (Tamás) ezek a limitek nem vonatkoznak — mindig unlimited.</p>
        <div className="grid grid-cols-2 gap-4 mb-8">
          {field("per_user_daily_cost_usd", "Napi USD hard cap", true)}
          {field("per_user_monthly_cost_usd", "Havi USD budget", true)}
        </div>

        <h3 className="font-heading text-[18px] font-semibold mb-1">Globális pilot becsült költségplafon</h3>
        <p className="text-[12.5px] text-[#a1a1a1] mb-5">Az <strong>összes</strong> becsült AI-költség (admin is) együtt — biztonsági puffer.</p>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {field("global_daily_cost_usd", "Globális napi becsült plafon (USD)", true)}
          {field("global_monthly_cost_usd", "Globális havi becsült plafon (USD)", true)}
        </div>
        <button onClick={save} disabled={saving} data-testid="pilot-settings-save"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white"
          style={{ background: "linear-gradient(135deg, #5e50f7 0%, #4F46E5 100%)", boxShadow: "0 12px 32px rgba(94,80,247,0.36)", fontSize: "14px" }}>
          <FloppyDisk size={15} weight="fill" /> {saving ? "Mentés…" : "Limitek mentése"}
        </button>
      </div>

      {/* ═══════ 2. ECU KORREKCIÓ / VALÓS FOGYÁS ═══════ */}
      <div className="rounded-2xl p-5 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.5)" }} data-testid="ecu-correction-settings-block">
        <div className="mb-4">
          <h3 className="font-heading text-[16px] font-semibold mb-1">Lezárt napi ECU korrekció</h3>
          <p className="text-[11px] text-[#7c7c8a]" data-testid="ecu-conversion-rules">Raw becsült ECU → hivatalos napi actual ECU → exact vagy rolling faktor → valós admin riport réteg.</p>
        </div>

        <div className="rounded-xl border border-white/[0.06] p-4 mb-5" style={{ background: "rgba(12,12,18,0.65)" }} data-testid="yesterday-calibration-editor">
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#7c7c8a] mb-1.5">Tegnapi ECU kalibráció</div>
              <div className="flex items-center gap-2 text-[17px] font-semibold text-white">
                <span>Dátum: <span className="text-[#c8c8d2]" data-testid="ecu-correction-selected-date">{calibrationDay?.date || ecuForm.date || "—"}</span></span>
                <button
                  type="button"
                  onClick={() => {
                    setShowDatePicker(true);
                    setTimeout(() => {
                      try { datePickerRef.current?.showPicker?.(); } catch (_error) { return; }
                    }, 0);
                  }}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/[0.08] text-[#a1a1a1] hover:text-white hover:border-white/[0.16]"
                  data-testid="ecu-correction-date-picker-toggle"
                  title="Másik lezárt nap kiválasztása"
                >
                  <CalendarBlank size={13} weight="regular" />
                </button>
              </div>
              {showDatePicker && (
                <div className="mt-2">
                  <input
                    ref={datePickerRef}
                    type="date"
                    value={ecuForm.date || defaultCalibrationDate}
                    max={defaultCalibrationDate}
                    onChange={(e) => onCalibrationDateChange(e.target.value)}
                    className="px-2.5 py-1.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[12px] text-[#c8c8d2] focus:outline-none focus:border-[#5e50f7]"
                    data-testid="ecu-correction-date-input"
                  />
                </div>
              )}
              <div className="mt-3 text-[12px] text-[#a1a1a1]">Becsült ECU összesen</div>
              <div className="text-[24px] font-semibold text-[#c8c8d2]" data-testid="yesterday-estimated-total-ecu">{fmtEcu(calibrationDay?.estimated_total_ecu)} ECU</div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <CorrectionFactorChip factor={calibrationDay?.correction_factor || 1} source={calibrationDay?.factor_source || "raw_1x"} testId="ecu-selected-factor-chip" />
                <span className="text-[10.5px] text-[#8a8a99]" data-testid="ecu-selected-factor-source">
                  {calibrationDay?.status_label || calibrationSourceMeta.label}
                </span>
              </div>
              <div className="mt-2 text-[10.5px] text-[#8a8a99]" data-testid="ecu-selected-updated-at">Utolsó frissítés: {calibrationDay?.updated_at ? fmtTs(calibrationDay.updated_at) : "—"}</div>
            </div>
            <div className="flex-1 max-w-[360px]">
              <label className="block mb-3">
                <span className="block text-[10.5px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Hivatalos napi actual ECU</span>
                <input type="number" min="0" step="0.01" value={ecuForm.actual_app_ecu} onChange={(e) => setEcuForm((v) => ({ ...v, actual_app_ecu: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13.5px] focus:outline-none focus:border-[#5e50f7]"
                  placeholder="pl. 66.53" data-testid="ecu-correction-app-ecu-input" />
              </label>
              <label className="block mb-3">
                <span className="block text-[10.5px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Megjegyzés</span>
                <input type="text" value={ecuForm.note} onChange={(e) => setEcuForm((v) => ({ ...v, note: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13.5px] focus:outline-none focus:border-[#5e50f7]"
                  placeholder="opcionális" data-testid="ecu-correction-note-input" />
              </label>
              <button onClick={saveEcu} disabled={savingEcu}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #5e50f7 0%, #4F46E5 100%)", opacity: savingEcu ? 0.7 : 1 }}
                data-testid="ecu-correction-save-button">
                <FloppyDisk size={14} weight="fill" /> {savingEcu ? "Mentés…" : "Mentés / Kalibrálás"}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-white/[0.05] overflow-hidden" style={{ background: "rgba(12,12,18,0.65)" }}>
          <div className="grid grid-cols-[110px_120px_120px_140px_110px_1fr_110px_72px] gap-3 px-4 py-2.5 border-b border-white/[0.06] text-[10.5px] tracking-[0.12em] uppercase text-[#7c7c8a]">
            <div>Dátum</div>
            <div className="text-right">Becsült ECU</div>
            <div className="text-right">Tényleges ECU</div>
            <div className="text-right">Faktor</div>
            <div>Forrás</div>
            <div>Megjegyzés</div>
            <div>Frissítve</div>
            <div className="text-right">Törlés</div>
          </div>
          {ecuItems.length === 0 ? (
            <div className="px-4 py-5 text-[12px] text-[#8a8a99]" data-testid="ecu-correction-empty-state">Még nincs mentett ECU korrekció.</div>
          ) : ecuItems.map((item) => (
            <div key={item.date} className="grid grid-cols-[110px_120px_120px_140px_110px_1fr_110px_72px] gap-3 px-4 py-3 border-b border-white/[0.04] last:border-b-0 text-[12px] items-center" data-testid={`ecu-correction-row-${item.date}`}>
              <div className="text-[#c8c8d2] font-medium">{item.date}</div>
              <div className="text-right text-[#c8c8d2]">{fmtEcu(item.estimated_total_ecu)} ECU</div>
              <div className="text-right text-[#a294ff]">{item.actual_app_ecu == null ? "—" : `${fmtEcu(item.actual_app_ecu)} ECU`}</div>
              <div className="text-right"><CorrectionFactorChip factor={item.correction_factor || 1} source={item.factor_source || "raw_1x"} testId={`ecu-correction-factor-${item.date}`} /></div>
              <div className="text-[#8a8a99]">{item.status_label || getFactorSourceMeta(item.factor_source).label}</div>
              <div className="text-[#8a8a99] truncate" title={item.note || ""}>{item.note || "—"}</div>
              <div className="text-[#8a8a99] text-[11px]">{item.updated_at ? fmtTs(item.updated_at) : "—"}</div>
              <div className="text-right">
                {item.has_saved_record ? (
                  <button
                    type="button"
                    onClick={() => deleteEcu(item.date)}
                    disabled={deletingEcuDate === item.date}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#fb7185] hover:bg-[#fb718522] disabled:opacity-40"
                    data-testid={`ecu-correction-delete-${item.date}`}
                    title="ECU rekord törlése"
                  >
                    <Trash size={14} />
                  </button>
                ) : <span className="text-[#7c7c8a]">—</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════ 3. MXC BELSŐ KALKULÁCIÓ + VÉDELEM (kompakt, alul) ═══════ */}
      <div className="rounded-2xl p-5 border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.5)" }}>
        <h3 className="font-heading text-[15px] font-semibold mb-1">MXC belső kalkuláció</h3>
        <p className="text-[11.5px] text-[#8a8a99] mb-4 max-w-[720px]">
          Az MXC most belső mérőszám. A becsült USD költségből számoljuk, hogy később pontosan be tudjuk lőni a kreditcsomagokat.
        </p>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <label className="block">
            <span className="block text-[10.5px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Alap induló MXC</span>
            <input type="number" step="1" min="0" value={creditCfg.default_initial_credit ?? ""}
              onChange={(e) => setCreditCfg((c) => ({ ...c, default_initial_credit: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13.5px] focus:outline-none focus:border-[#5e50f7]" />
            <span className="block text-[10.5px] text-[#6a6a80] mt-1.5">Új userek alap belső kerete (mérési beállítás).</span>
          </label>
          <label className="block">
            <span className="block text-[10.5px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Belső MXC váltó</span>
            <div className="flex items-center gap-2">
              <span className="text-[13.5px] text-[#a1a1a1]">1 USD =</span>
              <input type="number" step="1" min="1" value={creditCfg.mex_per_usd ?? ""}
                onChange={(e) => setCreditCfg((c) => ({ ...c, mex_per_usd: parseFloat(e.target.value) || 1 }))}
                className="flex-1 px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13.5px] focus:outline-none focus:border-[#5e50f7]" />
              <span className="text-[13.5px] text-[#a1a1a1]">MXC</span>
            </div>
            <span className="block text-[10.5px] text-[#6a6a80] mt-1.5">1 USD becsült AI-költség = X belső MXC. Nem user felé kommunikált ár.</span>
          </label>
          <label className="block">
            <span className="block text-[10.5px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">Becsült költség szorzó</span>
            <input type="number" step="0.05" min="0" value={creditCfg.cost_estimation_multiplier ?? 1}
              onChange={(e) => setCreditCfg((c) => ({ ...c, cost_estimation_multiplier: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13.5px] focus:outline-none focus:border-[#5e50f7]" />
            <span className="block text-[10.5px] text-[#6a6a80] mt-1.5">A dashboard becsült USD költségét korrigálja. Alapérték: 1.0. Csak akkor módosítsd, ha a dashboard tartósan eltér az Emergent tényleges levonásától.</span>
          </label>
        </div>

        <div className="mt-6 pt-5 border-t border-white/[0.05]">
          <div className="flex items-baseline justify-between mb-3">
            <h4 className="font-heading text-[13.5px] font-semibold">Csomag alapkeretek</h4>
            <span className="text-[10.5px] text-[#6a6a80]">Csak explicit user-csomagnál lépnek életbe. Meglévő user keretet deploy nem ír felül.</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(creditCfg.credit_plans || {}).filter(([key]) => ["MEX", "MEX+", "SPRINT", "EGYEDI"].includes(key)).map(([planKey, plan]) => {
              const mpu = creditCfg.mex_per_usd || 50;
              const dailyMxcVal = plan.daily_cost_usd != null ? Number((plan.daily_cost_usd * mpu).toFixed(1)) : 0;
              return (
              <div key={planKey} className="rounded-xl border border-white/[0.06] p-4" style={{ background: "rgba(15,15,22,0.6)" }} data-testid={`credit-plan-card-${planKey}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[12px] font-semibold" style={{ color: "#ffdb5c" }}>{planKey}</div>
                  <span className="text-[10px] text-[#8a8a99]" data-testid={`credit-plan-count-${planKey}`}>{planCounts[planKey] || 0} felhasználó</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-[10px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1">Havi MXC</span>
                    <input type="number" step="10" min="0" value={plan.initial_credit ?? 0} onChange={(e) => setPlanInitialCredit(planKey, e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13px] focus:outline-none focus:border-[#5e50f7]" data-testid={`credit-plan-initial-${planKey}`} />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1">Havi USD</span>
                    <input type="number" step="0.1" min="0" value={plan.monthly_cost_usd ?? 0} onChange={(e) => setPlanMonthlyUsd(planKey, e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13px] focus:outline-none focus:border-[#5e50f7]" data-testid={`credit-plan-monthly-${planKey}`} />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1">Napi MXC</span>
                    <input type="number" step="10" min="0" value={dailyMxcVal} onChange={(e) => setPlanDailyMxc(planKey, e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13px] focus:outline-none focus:border-[#5e50f7]" data-testid={`credit-plan-daily-mxc-${planKey}`} />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1">Napi USD</span>
                    <input type="number" step="0.1" min="0" value={plan.daily_cost_usd ?? 0} onChange={(e) => setPlanDailyUsd(planKey, e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13px] focus:outline-none focus:border-[#5e50f7]" data-testid={`credit-plan-daily-${planKey}`} />
                  </label>
                </div>
                <div className="text-[9.5px] text-[#6a6a80] mt-2">1 USD = {mpu} MXC · a MXC és USD mezők szinkronizálódnak</div>
              </div>
            );})}
          </div>
        </div>

        {/* MXC védelem — kompakt segmented control */}
        <div className="mt-6 pt-5 border-t border-white/[0.05]">
          <div className="flex items-baseline justify-between mb-2.5">
            <h4 className="font-heading text-[13.5px] font-semibold">MXC védelem</h4>
            <span className="text-[10.5px] text-[#6a6a80]">Alapértelmezés: Csak mérés · Admint sose blokkolja</span>
          </div>
          <div className="inline-flex rounded-xl p-1 border border-white/[0.06]" style={{ background: "rgba(15,15,22,0.6)" }} data-testid="guard-mode-segmented">
            {guardOptions.map((opt) => {
              const active = creditCfg.guard_mode === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setCreditCfg((c) => ({ ...c, guard_mode: opt.key }))}
                  data-testid={`guard-mode-${opt.key}`}
                  className="px-4 py-2 rounded-lg text-[12.5px] font-semibold transition"
                  style={{
                    background: active ? `${opt.color}22` : "transparent",
                    color: active ? opt.color : "#a1a1a1",
                    borderRight: opt.key === "monitor_only" ? "1px solid rgba(255,255,255,0.04)" : "none",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11.5px] text-[#8a8a99] mt-2.5 max-w-[720px]">
            {guardOptions.find((o) => o.key === creditCfg.guard_mode)?.desc || "Belső teszt mód aktív (fejlesztői beállítás). Az admin UI mostantól csak 2 fő módot mutat."}
          </p>
          {isLegacyMode && (
            <p className="text-[10.5px] text-[#a294ff] mt-1.5">
              ⓘ Jelenlegi mód: <b>{creditCfg.guard_mode}</b> (fejlesztői config). Válts <b>Csak mérés</b>-re az UI-ban való követhetőséghez.
            </p>
          )}
        </div>

        <button onClick={saveCredit} disabled={savingCredit} data-testid="credit-config-save"
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white text-[13px]"
          style={{ background: "linear-gradient(135deg, #a294ff, #5e50f7)", boxShadow: "0 10px 28px rgba(94,80,247,0.28)" }}>
          <FloppyDisk size={13} weight="fill" /> {savingCredit ? "Mentés…" : "MXC beállítások mentése"}
        </button>
      </div>
    </div>
  );
}

/* ─────────── Agent flags tab (megőrizve) ─────────── */
export function AgentFlagsTab() {
  const [flagsByKey, setFlagsByKey] = useState({});
  const load = async () => {
    const r = await api.get("/admin/agent-flags");
    const map = {};
    (r.data.flags || []).forEach((f) => { map[f.agent_key] = f; });
    setFlagsByKey(map);
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (key, status) => {
    try { await api.patch(`/admin/agent-flags/${key}`, { status }); toast.success("Frissítve."); load(); }
    catch { toast.error("Sikertelen."); }
  };
  const statusLabels = { live: "Live", admin_only: "Csak admin", hidden: "Rejtett" };
  const layerOrder = ["foundation", "attraction", "conversion"];
  const layerAccent = { foundation: "#5e50f7", attraction: "#75dff5", conversion: "#ffdb5c" };

  return (
    <div data-testid="agent-flags-tab">
      <h3 className="font-heading text-[18px] font-semibold mb-1">Ügynökök elérhetősége</h3>
      <p className="text-[12.5px] text-[#a1a1a1] mb-3">
        A változás azonnal hatályba lép — semmi deploy/restart nincs.
        Tester nézetben az <em>admin_only</em> kártyák színesen, „Hamarosan” pillel jelennek meg, a <em>hidden</em> kártyák egyáltalán nem.
      </p>
      <div
        className="mb-6 rounded-md px-3 py-2 text-[12px]"
        style={{
          background: "rgba(255,219,92,0.08)",
          border: "1px solid rgba(255,219,92,0.28)",
          color: "#d4b04a",
        }}
        data-testid="agent-flags-info-banner"
      >
        ⓘ A módosítások a bejelentkezett usereknél oldalfrissítés vagy tabra visszatérés után jelennek meg.
      </div>

      <div className="space-y-7">
        {layerOrder.map((layer) => {
          const agents = AGENT_REGISTRY.filter((a) => a.layer === layer);
          if (agents.length === 0) return null;
          const accent = layerAccent[layer];
          return (
            <div key={layer} data-testid={`agent-flags-layer-${layer}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
                <h4 className="font-heading text-[15px] font-semibold tracking-tight" style={{ color: accent }}>
                  {LAYER_LABELS[layer]}
                </h4>
                <div className="flex-1 h-px" style={{ background: `${accent}22` }} />
              </div>
              <div className="rounded-2xl overflow-hidden border border-white/[0.06]" style={{ background: "rgba(20,20,26,0.6)" }}>
                {agents.map((a) => {
                  const f = flagsByKey[a.backendKey];
                  const status = f?.status || "live";
                  return (
                    <div
                      key={a.backendKey}
                      data-testid={`agent-flag-row-${a.backendKey}`}
                      className="grid grid-cols-[1fr_2fr_160px] gap-4 px-5 py-4 items-center border-b border-white/[0.04] last:border-b-0"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: `${accent}22`, border: `1px solid ${accent}33` }}
                        >
                          {a.icon && <a.icon size={17} weight="duotone" color={accent} />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-[14px] truncate">{a.title}</div>
                          <div className="text-[#7c7c8a] text-[11px] font-mono truncate">{a.backendKey}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {["live", "admin_only", "hidden"].map((opt) => (
                          <button
                            key={opt}
                            onClick={() => setStatus(a.backendKey, opt)}
                            data-testid={`agent-flag-${a.backendKey}-${opt}`}
                            className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition ${
                              status === opt
                                ? opt === "live"
                                  ? "bg-[#22C55E22] text-[#4ade80]"
                                  : opt === "admin_only"
                                  ? "bg-[#5e50f722] text-[#a294ff]"
                                  : "bg-[#fb718522] text-[#fb7185]"
                                : "bg-white/[0.04] text-[#a1a1a1] hover:bg-white/[0.08]"
                            }`}
                          >
                            {statusLabels[opt]}
                          </button>
                        ))}
                      </div>
                      <div className="text-[11px] text-[#7c7c8a] text-right">
                        {f?.updated_by && <>módosította: {f.updated_by}<br /></>}
                        {f?.updated_at && new Date(f.updated_at).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ─────────── Sprint 3.22 v4 — Workflow lefedettség (átlag/flow) ─────────── */
export function WorkflowCoverageTab({ mode = "calibrated" }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [includeAdmin, setIncludeAdmin] = useState(false);
  const normalizedMode = normalizeAdminUsageMode(mode);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/workflow-coverage?include_admin_runs=${includeAdmin}&rolling_days=3&window_days=30`);
      setData(r.data);
    } catch { toast.error("Nem sikerült betölteni a workflow lefedettséget."); }
    finally { setBusy(false); }
  }, [includeAdmin]);
  useEffect(() => { load(); }, [load, normalizedMode]);
  useAdminUsageRefresh(load);
  if (busy || !data) return <div className="px-6 py-12 text-center text-[#a1a1a1] text-sm">Betöltés…</div>;

  const statusMap = {
    active:   { color: "#4ade80", label: "Aktív mérés" },
    no_data:  { color: "#a1a1a1", label: "Nincs elég adat" },
    planned:  { color: "#fbbf24", label: "Tervezett" },
    inactive: { color: "#fb7185", label: "Nem aktív" },
  };

  const byLayer = {};
  data.coverage.forEach((c) => {
    const lyr = c.layer || "Egyéb";
    if (!byLayer[lyr]) byLayer[lyr] = [];
    byLayer[lyr].push(c);
  });
  const layerOrder = ["Alapozás", "Vonzás", "Konverzió", "Egyéb"];
  const layerAccent = { "Alapozás": "#5e50f7", "Vonzás": "#75dff5", "Konverzió": "#ffdb5c" };

  const activeCount = data.coverage.filter((c) => c.status === "active").length;
  const noDataCount = data.coverage.filter((c) => c.status === "no_data").length;
  const plannedCount = data.coverage.filter((c) => c.status === "planned").length;
  const enoughCount = data.coverage.filter((c) => c.data_status === "enough_data").length;

  const grid = "grid-cols-[minmax(150px,1fr)_minmax(170px,1.1fr)_120px_90px_90px_170px_170px_140px]";
  return (
    <div data-testid="workflow-coverage-tab">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-heading text-[18px] font-semibold">Workflow ár (átlag / teljes flow) — utolsó {data.window_days} nap</h3>
        <label className="flex items-center gap-2 text-[11.5px] text-[#a1a1a1] cursor-pointer" data-testid="wf-toggle-admin-runs">
          <input type="checkbox" checked={includeAdmin} onChange={(e) => setIncludeAdmin(e.target.checked)} data-testid="wf-toggle-admin-runs-input" />
          Admin tesztek is
        </label>
      </div>
      <p className="text-[12.5px] text-[#a1a1a1] mb-4">
        Egy „teljes flow” = egy `workflow_run_id` alatt lévő LLM hívások összege. Az átlagár prioritásban {data.rolling_days} napos rolling átlag a teljes futásokból,
        ha nincs elég adat → legutóbbi teljes flow. Refine / regenerate külön van (`Partial`), nem torzítja az átlagot.
        <span className="ml-1">Aktív nézet: <b>{normalizedMode === "estimated" ? "raw becslés" : "valós ECU-korrigált réteg"}</b>.</span>
        {!includeAdmin && data.excluded_admin_emails?.length > 0 && <> Admin ({data.excluded_admin_emails.length} email) kizárva.</>}
      </p>
      <div className="grid grid-cols-4 gap-3 mb-6">
        <MiniStat label="Összes workflow" value={data.coverage.length} />
        <MiniStat label="Aktív mérés" value={activeCount} accent="#4ade80" />
        <MiniStat label="Elegendő adat" value={enoughCount} accent="#4ade80" />
        <MiniStat label="Nincs / tervezett" value={noDataCount + plannedCount} accent="#a1a1a1" />
      </div>

      <div className="space-y-6">
        {layerOrder.map((lyr) => {
          const rows = byLayer[lyr] || [];
          if (!rows.length) return null;
          const accent = layerAccent[lyr] || "#a1a1a1";
          return (
            <div key={lyr} data-testid={`workflow-coverage-layer-${lyr}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
                <h4 className="font-heading text-[14px] font-semibold" style={{ color: accent }}>{lyr}</h4>
                <div className="flex-1 h-px" style={{ background: `${accent}22` }} />
              </div>
              <div className="rounded-xl border border-white/[0.06] overflow-hidden" style={{ background: "rgba(20,20,26,0.5)" }}>
                <div className={`grid ${grid} gap-3 px-4 py-2.5 border-b border-white/[0.06] text-[10.5px] tracking-[0.14em] uppercase text-[#7c7c8a]`}>
                  <div>Főkártya</div><div>Alkártya</div><div>Státusz</div>
                  <div className="text-right">Teljes fut.</div>
                  <div className="text-right">Részleges</div>
                  <div className="text-right">Átlag / flow</div>
                  <div className="text-right">Összes 30n</div>
                  <div>Adat minőség</div>
                </div>
                {[...rows].sort((a, b) => ((normalizedMode === "estimated" ? b.total_mxc_30d : b.total_vf_mxc_30d) || 0) - ((normalizedMode === "estimated" ? a.total_mxc_30d : a.total_vf_mxc_30d) || 0) || (b.full_run_count || 0) - (a.full_run_count || 0)).map((c) => {
                  const st = statusMap[c.status] || statusMap.no_data;
                  const dst = WORKFLOW_DATA_STATUS_LABEL[c.data_status] || WORKFLOW_DATA_STATUS_LABEL.no_data;
                  return (
                    <div key={c.slug} className={`grid ${grid} gap-3 px-4 py-3 border-b border-white/[0.04] last:border-b-0 items-center text-[12.5px]`}>
                      <div className="text-[#c8c8d2]">{c.main_card}</div>
                      <div className="text-[#a294ff]">{c.sub_card}</div>
                      <div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold" style={{ background: `${st.color}18`, color: st.color }} data-testid={`wf-status-${c.slug}`}>
                          {st.label}
                        </span>
                      </div>
                      <div className="text-right text-[13px] font-semibold text-[#c8c8d2]">{c.full_run_count}</div>
                      <div className="text-right text-[12px] text-[#a1a1a1]">{c.partial_run_count}</div>
                      <WorkflowCoverageMetricCell
                        mxc={normalizedMode === "estimated" ? c.average_full_run_cost_mxc : c.average_full_run_vf_mxc}
                        ecu={normalizedMode === "estimated" ? c.average_full_run_estimated_ecu : c.average_full_run_actual_platform_ecu}
                        usd={normalizedMode === "estimated" ? c.average_full_run_cost_usd : c.average_full_run_vf_usd}
                        testId={`wf-avg-cost-${c.slug}`}
                        emptyText={c.partial_run_count > 0 ? "Csak részadat" : "—"}
                      />
                      <WorkflowCoverageMetricCell
                        mxc={normalizedMode === "estimated" ? c.total_mxc_30d : c.total_vf_mxc_30d}
                        ecu={normalizedMode === "estimated" ? c.total_estimated_ecu_30d : c.total_effective_ecu_30d}
                        usd={normalizedMode === "estimated" ? c.total_usd_30d : c.total_vf_usd_30d}
                        testId={`wf-total-cost-${c.slug}`}
                      />
                      <div>
                        <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: `${dst.color}18`, color: dst.color }} title={`cost_source: ${c.cost_source}`}>
                          {dst.label}
                        </span>
                        {c.last_at && <div className="text-[10px] text-[#7c7c8a] mt-0.5">{fmtTs(c.last_at)}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
